import { AxiosResponse } from "axios";

import {
  DataRequestSubCategory,
  ReviewEntityType,
} from "../constants/pcfEntityMapping";
import { SoilDataApiResponse } from "../types/api";
import {
  CreateFarmCIActionItemPayload,
  CreateFieldCIActionItemPayload,
} from "../types/improve";
import {
  BackendProjectScope,
  CreateProjectPayload,
  ProjectTypeType,
  TaskStatus,
  UpdateTaskStepPayload,
} from "../types/projects";
import { ModalEntityType } from "../types/reviewData";
import coreAPI from "@/datasources/coreAPI";
import {
  CommentsFromAPI,
  CreateCommentDto,
  CreateCommentResponse,
  CreateEnterpriseValueDriver,
  CreateRegisteredInterestPayload,
  CustomUnit,
  DeleteCommentDto,
  EditCommentDto,
  EnterpriseHarvestCycle,
  EnterprisePreferences,
  EnterpriseReportsResponse,
  FinancialCategory,
  PrePropPayloadType,
  ProjectActivityEntry,
  QuestionResponsePayload,
  ReportProjectType,
  RoleType,
  ThreadCommentsFromAPI,
  UpdateNotificationPreferenceDto,
  UserType,
  YieldCoverageResponse,
  YieldFilters,
} from "@/types/api";
import { InviteMemberPayload, UpdateMemberPayload } from "@/types/members";
import { PinnedItem } from "@/types/pinnedItems";
import { RawCustomerDataType } from "@/types/reviewData";

// Inline utility from deleted reviewData.tsx
const toUpperSnakeCase = (str: string): string => {
  return str
    .replace(/-/g, "_")
    .split(/(?=[A-Z])/)
    .join("_")
    .toUpperCase();
};

// ==================================
// USER AND ROLE
// ==================================

export interface ExtractRoleInfoReturn {
  roleType: RoleType | null;
  enterpriseId: number | null;
  organisationId: number | null;
  farmId: number | null;
}

/**
 * @param user Extracts the roleType and roleId from the userWithUserRoles object
 */
export const extractRoleInfo = (user: any): ExtractRoleInfoReturn => {
  // This login supports SUPER_ADMIN, ENTERPRISE_ADMIN, ORGANISATION_ADMIN, and FARM_ADMIN roles
  // that are linked to a single role

  // For super admins with access to one enterprise (hort show demo), auto select that enterprise
  const superAdminEnterpriseIds = user?.userRoles
    ?.map((userRole: any) => {
      if (userRole?.role?.type === "SUPER_ADMIN") return userRole.enterpriseId;
      return undefined;
    })
    .filter((enterpriseId: number) => !!enterpriseId);

  // If there are multiple enterprise roles that a user has - let the user choose an enterprise
  const enterpriseIds = user?.userRoles
    ?.map((userRole: any) => {
      if (userRole?.role?.type === "ENTERPRISE_ADMIN")
        return userRole.enterpriseId;
      return undefined;
    })
    .filter((enterpriseId: number) => !!enterpriseId);

  if (enterpriseIds?.length >= 1) {
    return {
      roleType: "ENTERPRISE_ADMIN",
      enterpriseId: enterpriseIds?.length === 1 ? enterpriseIds[0] : null,
      organisationId: null,
      farmId: null,
    };
  }

  // If there is no enterpriseId - let's check if the user has an organisationId
  // Again, if there are multiple roles, we take the first one.
  const organisationAdminRole = user?.userRoles?.find(
    (userRole: { organisationId: any }) => userRole.organisationId !== null
  );

  const farmAdminRole = user?.userRoles?.find(
    (userRole: any) => userRole?.role?.type === "FARM_ADMIN"
  );

  if (organisationAdminRole) {
    return {
      roleType: "ORGANISATION_ADMIN",
      enterpriseId: organisationAdminRole?.organisation?.enterpriseId,
      organisationId: organisationAdminRole?.organisationId,
      farmId: null,
    };
  }

  if (farmAdminRole) {
    return {
      roleType: "FARM_ADMIN",
      enterpriseId: farmAdminRole?.farm?.organisation?.enterpriseId,
      organisationId: farmAdminRole?.farm?.organisationId,
      farmId: farmAdminRole?.farmId || null,
    };
  }

  const superAdminRole = user?.userRoles?.find(
    (userRole: any) => userRole?.role?.type === "SUPER_ADMIN"
  );
  if (superAdminRole) {
    return {
      roleType: "SUPER_ADMIN",
      enterpriseId:
        superAdminEnterpriseIds.length === 1
          ? superAdminEnterpriseIds[0]
          : null,
      organisationId: null,
      farmId: null,
    };
  }

  return {
    roleType: null,
    enterpriseId: null,
    organisationId: null,
    farmId: null,
  };
};

export const fetchMyUser = async () => {
  try {
    const { data: userWithUserRoles } = await coreAPI.get(`/users/me`);
    const { roleType } = extractRoleInfo(userWithUserRoles);
    if (roleType === null)
      throw new Error("User does not have a supported role.");
    else return userWithUserRoles;
  } catch (err: any) {
    if (err?.response?.status === 404) {
      throw new Error("User not found");
    }
    throw new Error(err.message);
  }
};

export const setFirstTimePassword = async (
  email: string,
  verificationToken: string,
  password: string
) =>
  coreAPI.post(`/users/first-time-password`, {
    email,
    verificationToken,
    password,
  });

export const registerInterest = async (
  payload: CreateRegisteredInterestPayload
) => {
  const { data } = await coreAPI.post(`/registered-interests`, payload);
  return data;
};

export const deregisterInterest = async () => {
  const { data } = await coreAPI.delete(`/registered-interests`);
  return data;
};

export const findInterestedUser = async () => {
  const { data } = await coreAPI.get(`/registered-interests`);
  return data;
};

export const updateUserDetails = async (
  userId: string,
  data: Record<string, any>
) => {
  const { data: updatedData } = await coreAPI.patch(`/users/${userId}/`, data);
  return updatedData;
};

export const updateProfilePicture = async (
  user: UserType,
  profilePictureUrl: string
) => {
  const { data: updatedData } = await coreAPI.patch(`/users/${user.id}/`, {
    profilePictureUrl,
  });
  return updatedData;
};

export const updateUserPreference = async (
  userId: string,
  preferenceKey: string,
  preferenceValue: any
) => {
  const { data: updatedData } = await coreAPI.patch(`/users/${userId}/`, {
    [preferenceKey]: preferenceValue,
  });
  return updatedData;
};

export const updateNotificationPreferences = (
  userId: string,
  preferences: UpdateNotificationPreferenceDto
) => {
  return coreAPI.patch(
    `/users/${userId}/notification-preferences`,
    preferences
  );
};

// New notification system endpoints
export const getNotifications = async (params?: {
  cursor?: number;
  limit?: number;
  category?: string;
  module?: string;
  unreadOnly?: boolean;
}) => {
  const { data } = await coreAPI.get("/notifications", { params });
  return data;
};

export const getUnreadNotificationCount = async () => {
  const { data } = await coreAPI.get("/notifications/unread-count");
  return data;
};

export const markNotificationAsRead = async (notificationId: number) => {
  const { data } = await coreAPI.patch(`/notifications/${notificationId}/read`);
  return data;
};

export const markAllNotificationsAsRead = async () => {
  const { data } = await coreAPI.patch("/notifications/read-all");
  return data;
};

export const getNotificationPreferences = async () => {
  const { data } = await coreAPI.get("/notifications/preferences");
  return data;
};

export const updateNotificationPreference = async (preference: {
  type: string;
  channel: string;
  enabled: boolean;
}) => {
  const { data } = await coreAPI.put("/notifications/preferences", preference);
  return data;
};

export const updateUserProfilePicture = async (
  userId: string,
  profilePicture: File
) => {
  const formData = new FormData();
  formData.append("file", profilePicture);
  const { data } = await coreAPI.patch(
    `/users/${userId}/profile-pictures`,
    formData
  );
  return data;
};

export const deleteUserProfilePicture = async (userId: string) => {
  const { data } = await coreAPI.delete(`/users/${userId}/profile-pictures`);
  return data;
};

export const fetchMentionableUsers = async () => {
  const { data } = await coreAPI.get(`/users/mentionable`);
  return data;
};

export const updateUserState = async (
  userId: string,
  data: {
    stateName: string;
    value: boolean;
  }
) => {
  const { data: updatedData } = await coreAPI.patch(
    `/users/${userId}/states`,
    data
  );
  return updatedData;
};

export const updateUserStatesBulk = async (
  userId: string,
  states: Record<string, boolean>
) => {
  const statesArray = Object.entries(states).map(([stateName, value]) => ({
    stateName,
    value,
  }));
  const { data } = await coreAPI.patch(`/users/${userId}/states/bulk`, {
    states: statesArray,
  });
  return data;
};

// ==================================
// ENTERPRISE
// ==================================

export const fetchAllOrgsInEnt = async (entId: string) => {
  const { data } = await coreAPI.get(`/enterprises/${entId}/organisations`);
  return data;
};

export const fetchAllFarmsInEnt = async (entId: string) => {
  const { data } = await coreAPI.get(`/enterprises/${entId}/farms`);
  return data;
};

export type PlantingsMetadataResponse = {
  farms: Array<{ id: number; name: string }>;
  crops: Array<{ id: number; name: string }>;
  varieties: Array<{ id: number; name: string; cropId: number }>;
  plantings: Array<{
    id: number;
    farmId: number | null;
    cropId: number | null;
    cropVarietyId: number | null;
    startDate: string | null;
    endDate: string | null;
  }>;
};

export const fetchPlantingsMetadata = async (
  entId: string
): Promise<PlantingsMetadataResponse> => {
  const { data } = await coreAPI.get(
    `/enterprises/${entId}/data-points/meta-data?onlyWithPlantings=true`
  );
  return data;
};

export const fetchFarmsInEntNotEngagedInCIItem = async (
  cIItemId: number,
  entId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${entId}/farms/not-engaged/${cIItemId}`
  );
  return data;
};

export const fetchAllFieldsInEnt = async (entId: string) => {
  const { data } = await coreAPI.get(`/enterprises/${entId}/fields`);
  return data;
};

export const fetchFieldsInEntNotEngagedInCIItem = async (
  cIItemId: number,
  entId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${entId}/fields/not-engaged/${cIItemId}`
  );
  return data;
};

// ==================================
// YIELD
// ==================================

export const fetchYieldData = async (
  enterpriseId: string,
  filters?: YieldFilters
) => {
  const { data } = await coreAPI.get(`/enterprise/${enterpriseId}/yields`, {
    params: {
      ...(filters?.startDate && { startDate: filters.startDate.toISOString() }),
      ...(filters?.endDate && { endDate: filters.endDate.toISOString() }),
      ...(filters?.crop?.id && { cropIds: filters.crop.id }),
      ...(filters?.varietyIds?.length && {
        varietyIds: filters.varietyIds.join(","),
      }),
      ...(filters?.farmIds?.length && { farmIds: filters.farmIds.join(",") }),
      ...(filters?.fieldIds?.length && {
        fieldIds: filters.fieldIds.join(","),
      }),
      ...(filters?.currentPlantingsOnly && {
        currentPlantingsOnly: filters.currentPlantingsOnly,
      }),
    },
  });
  return data;
};

export const fetchYieldDashboard = async (
  enterpriseId: string,
  filters?: YieldFilters
) => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/yields/dashboard`,
    {
      params: {
        ...(filters?.startDate && {
          startDate: filters.startDate.toISOString(),
        }),
        ...(filters?.endDate && { endDate: filters.endDate.toISOString() }),
        ...(filters?.crop?.id && { cropIds: filters.crop.id }),
        ...(filters?.varietyIds?.length && {
          varietyIds: filters.varietyIds.join(","),
        }),
        ...(filters?.farmIds?.length && { farmIds: filters.farmIds.join(",") }),
        ...(filters?.fieldIds?.length && {
          fieldIds: filters.fieldIds.join(","),
        }),
        ...(filters?.currentPlantingsOnly && {
          currentPlantingsOnly: filters.currentPlantingsOnly,
        }),
        ...(filters?.selectedYears?.length && {
          selectedYears: filters.selectedYears.join(","),
        }),
      },
    }
  );
  return data;
};

export const fetchYieldCoverageData = async (
  enterpriseId: string,
  filters?: YieldFilters
) => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/yields/coverage`,
    {
      params: {
        startDate: filters?.startDate?.toISOString(),
        endDate: filters?.endDate?.toISOString(),
        cropIds: filters?.crop?.id,
        varietyIds: filters?.varietyIds?.length
          ? filters.varietyIds.join(",")
          : undefined,
        farmIds: filters?.farmIds?.length
          ? filters.farmIds.join(",")
          : undefined,
      },
    }
  );
  return data as YieldCoverageResponse;
};

export interface CropInputsParams {
  years?: number[]; // Multi-year support
  cropId?: number; // Filter by specific crop
  farmIds?: number[]; // Filter by farms
}

export const fetchCropInputData = async (
  enterpriseId: string,
  params?: CropInputsParams
) => {
  const searchParams = new URLSearchParams();

  // Multi-year support
  if (params?.years && params.years.length > 0) {
    searchParams.append("years", params.years.join(","));
  }

  // Crop filter
  if (params?.cropId !== undefined) {
    searchParams.append("cropId", params.cropId.toString());
  }

  // Farm filter
  if (params?.farmIds && params.farmIds.length > 0) {
    searchParams.append("farmIds", params.farmIds.join(","));
  }

  const queryString = searchParams.toString();
  const url = `/enterprise/${enterpriseId}/crop-inputs${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await coreAPI.get(url);
  return data;
};

export interface CropInputsDashboardParams {
  years?: number[];
  cropId?: number;
  farmIds?: number[];
}

export interface CropInputsDashboardResponse {
  applicationTimeline: Array<{
    month: number;
    monthName: string;
    category: string;
    categoryLabel: string;
    count: number;
  }>;
  categoryBreakdown: Array<{
    category: string;
    categoryLabel: string;
    count: number;
    percentage: number;
  }>;
  topProducts: Array<{
    productLabel: string;
    category: string;
    categoryLabel: string;
    applications: number;
    fieldsApplied: number;
  }>;
  fieldIntensity: Array<{
    fieldId: number;
    fieldName: string;
    farmId: number;
    farmName: string;
    totalApplications: number;
    area: number;
    areaUnit: string;
    applicationsPerArea: number;
    categoryBreakdown: Array<{
      category: string;
      categoryLabel: string;
      count: number;
    }>;
  }>;
  availableYears: number[];
  totalApplications: number;
}

export const fetchCropInputsDashboard = async (
  enterpriseId: string,
  params?: CropInputsDashboardParams
): Promise<CropInputsDashboardResponse> => {
  const searchParams = new URLSearchParams();

  if (params?.years && params.years.length > 0) {
    // NestJS expects array format: years[]=2024&years[]=2025
    params.years.forEach((year) => {
      searchParams.append("years[]", year.toString());
    });
  }
  if (params?.cropId !== undefined) {
    searchParams.append("cropId", params.cropId.toString());
  }
  if (params?.farmIds && params.farmIds.length > 0) {
    // NestJS expects array format: farmIds[]=1&farmIds[]=2
    params.farmIds.forEach((farmId) => {
      searchParams.append("farmIds[]", farmId.toString());
    });
  }

  const queryString = searchParams.toString();
  const url = `/enterprise/${enterpriseId}/crop-inputs/dashboard${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await coreAPI.get(url);
  return data;
};

export const fetchCropData = async (
  enterpriseId: string,
  includeAll = false
) => {
  const params = includeAll ? { includeAll: true } : {};
  const { data } = await coreAPI.get(`/enterprises/${enterpriseId}/crops`, {
    params,
  });
  return data;
};
// ==================================
// ORGANISATION
// ==================================

export const fetchAllFarmsInOrg = async (
  entId: string,
  organisationId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${entId}/organisations/${organisationId}/farms`
  );
  return data;
};

export const fetchFarmsInOrgNotEngagedInCIItem = async (
  cIItemId: number,
  entId: string,
  organisationId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${entId}/organisations/${organisationId}/farms/not-engaged/${cIItemId}`
  );
  return data;
};

export const fetchAllFieldsInOrg = async (
  entId: string,
  organisationId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${entId}/organisations/${organisationId}/fields`
  );
  return data;
};

export const fetchFieldsInOrgNotEngagedInCIItem = async (
  cIItemId: number,
  entId: string,
  organisationId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${entId}/organisations/${organisationId}/fields/not-engaged/${cIItemId}`
  );
  return data;
};

// ==================================
// CONTINUOUS IMPROVEMENT
// ==================================

export const fetchAllCIItems = async (version: number) => {
  const { data: CIItemsFetched } = await coreAPI.get(
    `/ci-items/version/${version}`
  );
  return CIItemsFetched;
};

export const assignCIActionItemToFields = async ({
  selectedFieldIds,
  ciItemId,
}: CreateFieldCIActionItemPayload) => {
  const payload = {
    fieldAssignments: selectedFieldIds.map((fieldId) => ({ fieldId })),
  };

  const { data } = await coreAPI.post(
    `/ci-action-items/${ciItemId}/assignToFields`,
    payload
  );
  return data;
};

export const assignCIActionItemToFarms = async ({
  selectedFarmIds,
  ciItemId,
}: CreateFarmCIActionItemPayload) => {
  const payload = {
    farmAssignments: selectedFarmIds.map((farmId) => ({ farmId })),
  };

  const { data } = await coreAPI.post(
    `/ci-action-items/${ciItemId}/assignToFarms`,
    payload
  );
  return data;
};

export const fetchCiActionItem = async (id: string) => {
  const { data } = await coreAPI.get(`/ci-action-items/${id}`);
  return data;
};
export const fetchCiActionItemWithResources = async (id: string) => {
  const { data } = await coreAPI.get(`/ci-action-items/${id}/resources`);
  return data;
};

export const fetchCiItem = async (id: string) => {
  const { data } = await coreAPI.get(`/ci-items/${id}`);
  return data;
};

export const fetchCiItemWithResources = async (
  id: string,
  engagedFarms?: boolean,
  engagedFields?: boolean
) => {
  const { data } = await coreAPI.get(
    `/ci-items/${id}/resources/?engagedFarms=${engagedFarms}&engagedFields=${engagedFields}`
  );
  return data;
};

type CreateCommentPayload = {
  ciActionItemId: number;
  comment: string;
  authorId: number;
  actionUrl?: string;
  parentCommentId?: number;
  mentions?: { mentionedUserId: number }[];
};

type UpdateCommentPayload = {
  ciActionItemId: number;
  comment: string;
  commentId: number;
  mentions?: { mentionedUserId: number }[];
};

export const fetchCommentsForActionItem = async (
  ciActionItemId: number
): Promise<CommentsFromAPI[]> => {
  const { data } = await coreAPI.get(
    `/ci-action-items/${ciActionItemId}/comments`
  );
  return data;
};

export const postCommentForActionItem = async (
  createCommentPayload: CreateCommentPayload
) => {
  try {
    const response = await coreAPI.post(
      `ci-action-items/${createCommentPayload.ciActionItemId}/comments`,
      createCommentPayload
    );
    const newComment = response.data;

    return newComment;
  } catch (e) {
    console.error("Error posting comment:", e);
    throw e;
  }
};

export const editCommentForActionItem = async (
  updateCommentPayload: UpdateCommentPayload
) => {
  try {
    await coreAPI.patch(
      `ci-action-items/${updateCommentPayload.ciActionItemId}/comments`,
      updateCommentPayload
    );
  } catch (e) {
    console.error("Error editing comment:", e);
    throw e;
  }
};

export const deleteCommentForActionItem = async (commentId: number) => {
  try {
    await coreAPI.delete(`ci-action-items/comments/${commentId}`);
  } catch (e) {
    console.error("Error deleting comment:", e);
  }
};

export const getCommentsForActionItem = async (
  ciActionItemId: number
): Promise<CommentsFromAPI[]> => {
  const { data } = await coreAPI.get(
    `/ci-action-items/${ciActionItemId}/comments`
  );
  return data;
};

export const fetchFarmSAMCIItems = async (farmId: number) => {
  const { data } = await coreAPI.get(`/sam-ci-items/farm/${farmId}`);
  return data;
};

export const fetchSAMCIItem = async (id: number) => {
  const { data } = await coreAPI.get(`/sam-ci-items/${id}`);
  return data;
};

export const fetchCiVerificationData = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(
    `/ci-action-items/enterprise/${enterpriseId}/verification-data`
  );
  return data;
};

// ==================================
// ENTERPRISES
// ==================================

export const getAllEnterprises = async (userId?: number) => {
  const { data } = await coreAPI.get(
    `/enterprises`,
    userId
      ? {
          params: { userId },
        }
      : undefined
  );
  return data;
};

export const getEnterpriseData = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(`/enterprises/${enterpriseId}`);
  return data;
};

export const getEnterpriseOrganisationData = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/organisations/`
  );
  return data;
};

export const getEnterpriseSummary = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(`/enterprises/${enterpriseId}/summary`);
  return data;
};

// ==================================
// ORGANISATIONS
// ==================================

export const getSingleOrganisationData = async (
  enterpriseId: string,
  organisationId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/organisations/${organisationId}`
  );
  return data;
};

export const getFarmsFieldCiActionItemsByOrganisations = async (
  enterpriseId: string,
  organisationId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/organisations/${organisationId}/farms-with-all-ci-action-items/2`
  );
  return data;
};

export const getAllFieldsForEnterprise = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(`/enterprises/${enterpriseId}/fields`);
  return data;
};

// New state-related endpoints
export const getEnterpriseStates = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(`/enterprises/${enterpriseId}/states`);
  return data;
};

export const getEnterpriseState = async (
  enterpriseId: string,
  stateName: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/states/${stateName}`
  );
  return data;
};

export const setEnterpriseState = async (
  enterpriseId: string,
  stateName: string,
  value: boolean
) => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/states/${stateName}`,
    {
      value,
    }
  );
  return data;
};

// ==================================
// FARMS
// ==================================

export const fetchFarmData = async (
  entId: string,
  orgId: string,
  farmId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${entId}/organisations/${orgId}/farms/${farmId}`
  );
  return data;
};

export const getFarmDataByEnterprise = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(`/enterprises/${enterpriseId}/farms`);
  return data;
};

export interface CropWithThresholds {
  id: number;
  name: string;
  gddBaseTempF: number;
  chillTempLowF: number;
  chillTempHighF: number;
  sunburnTempModerateF: number | null;
  sunburnTempHighF: number | null;
  diseaseHumidityPct: number | null;
  diseasePrecipIn: number | null;
}

export interface FarmWithCoordinates {
  id: number;
  name: string;
  lat: number | null;
  lng: number | null;
  crops: CropWithThresholds[];
}

/**
 * Get farms with their centroid coordinates
 * Used for weather widget location selection
 */
export const getFarmsWithCoordinates = async (
  enterpriseId: string
): Promise<FarmWithCoordinates[]> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/farms/coordinates`
  );
  return data;
};

export const getFarmsFieldCiActionItemsByEnterprise = async (
  enterpriseId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/farms-with-all-ci-action-items/2`
  );
  return data;
};

export const getSingleFarmFieldCiActionItems = async (
  enterpriseId: string,
  organisationId: string,
  farmId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/organisations/${organisationId}/farms/${farmId}/with-all-ci-action-items/2`
  );
  return data;
};

// ==================================
// FIELDS
// ==================================

export const getSingleFieldData = async (
  enterpriseId: string,
  organisationId: string,
  fieldId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/organisations/${organisationId}/fields/${fieldId}`
  );
  return data;
};

// ==================================
// RESOURCES
// ==================================

export const fetchAllResourcesForCiItem = async (ciItemId: string) => {
  const { data } = await coreAPI.get(`/ci-items/${ciItemId}/resources`);
  return data;
};

// ==================================
// RAW CUSTOMER DATA
// ==================================

export type RawDataFilters = {
  enterpriseId?: string;
  projectTaskId?: string;
  dataTag?: string;
};

export const fetchAllRawCustomerData = async (filters: RawDataFilters = {}) => {
  const params = new URLSearchParams();

  if (filters.enterpriseId) {
    params.append("enterpriseId", filters.enterpriseId);
  }

  if (filters.projectTaskId) {
    params.append("projectTaskId", filters.projectTaskId);
  }

  if (filters.dataTag) {
    // Remove the encodeURIComponent since URLSearchParams handles encoding
    params.append("dataTag", filters.dataTag);
  }

  const queryString = params.toString();
  const url = `raw-data${queryString ? `?${queryString}` : ""}`;

  const { data } = await coreAPI.get(url);
  return data;
};

// TODO: these endpoints should all be replaced with the fetchAllRawCustomerData endpoint once refactored

export interface RawDataEnterpriseFilters {
  status?: string;
  farmId?: number;
  organisationId?: number;
  tags?: string[];
}

export const fetchRawCustomerData = async (
  enterpriseId: string,
  filters?: RawDataEnterpriseFilters
) => {
  // Validate enterpriseId to prevent invalid API calls
  if (
    !enterpriseId ||
    enterpriseId === "undefined" ||
    !/^\d+$/.test(enterpriseId)
  ) {
    throw new Error(`Invalid enterpriseId: ${enterpriseId}`);
  }

  const params = new URLSearchParams();

  if (filters?.status) {
    params.append("status", filters.status);
  }
  if (filters?.farmId) {
    params.append("farmId", String(filters.farmId));
  }
  if (filters?.organisationId) {
    params.append("organisationId", String(filters.organisationId));
  }
  if (filters?.tags && filters.tags.length > 0) {
    filters.tags.forEach((tag) => params.append("tags", tag));
  }

  const queryString = params.toString();
  const url = `raw-data/enterprise/${enterpriseId}${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await coreAPI.get(url);
  return data;
};

// Entity count response for file details
export interface EntityCount {
  entityType: string;
  entityLabel: string;
  total: number;
  needsReview: number;
}

export const fetchFileEntityCounts = async (
  fileId: number
): Promise<EntityCount[]> => {
  const { data } = await coreAPI.get(`raw-data/${fileId}/entity-counts`);
  return data;
};

// Status history event from audit log
export interface StatusHistoryEvent {
  id: number;
  changeTimestamp: string;
  executedBy: "FOREST_ADMIN" | "BX_HUB_USER" | "CUSTOMER" | "ETL_PIPELINE";
  userId: number | null;
  changes: {
    status: [string | null, string];
  };
  metadata: {
    changeType: string;
    newStatus: string;
    batchId?: string;
    entityType?: string;
    reason?: string;
    backfilled?: boolean;
  } | null;
}

export const fetchFileStatusHistory = async (
  fileId: number
): Promise<StatusHistoryEvent[]> => {
  const { data } = await coreAPI.get(`raw-data/${fileId}/status-history`);
  return data;
};

// ==================================
// DATA AVAILABILITY
// ==================================

export interface DataAvailabilityMatrixResponse {
  columns: Array<{
    id: number;
    name: string;
    type: "organisation" | "farm";
  }>;
  rows: Array<{
    entity: string;
    entityLabel: string;
    locked: boolean;
    cells: Array<{
      columnId: number;
      status:
        | "available"
        | "partial"
        | "needs-input"
        | "processing"
        | "no-data"
        | "locked";
      recordCount?: number;
      lastUpdated?: string;
      unverifiedCount?: number;
      totalDRIs?: number;
      completeDRIs?: number;
      processingFileCount?: number;
    }>;
  }>;
}

export const fetchDataAvailabilityMatrix = async (
  enterpriseId: string,
  options?: {
    groupBy?: "organisation" | "farm";
    year?: number;
    farmIds?: number[];
    projectId?: number;
  }
): Promise<DataAvailabilityMatrixResponse> => {
  // Validate enterpriseId to prevent invalid API calls
  if (
    !enterpriseId ||
    enterpriseId === "undefined" ||
    !/^\d+$/.test(enterpriseId)
  ) {
    throw new Error(`Invalid enterpriseId: ${enterpriseId}`);
  }

  const searchParams = new URLSearchParams();
  if (options?.groupBy) searchParams.set("groupBy", options.groupBy);
  if (options?.year) searchParams.set("year", options.year.toString());
  if (options?.farmIds && options.farmIds.length > 0) {
    // Append each farm ID as a separate query param for array support
    options.farmIds.forEach((id) =>
      searchParams.append("farmIds", id.toString())
    );
  }
  if (options?.projectId)
    searchParams.set("projectId", options.projectId.toString());
  const params = searchParams.toString() ? `?${searchParams.toString()}` : "";

  const { data } = await coreAPI.get(
    `enterprise/${enterpriseId}/data-availability/matrix${params}`
  );
  return data;
};

export const fetchAvailableYears = async (
  enterpriseId: string
): Promise<number[]> => {
  if (
    !enterpriseId ||
    enterpriseId === "undefined" ||
    !/^\d+$/.test(enterpriseId)
  ) {
    throw new Error(`Invalid enterpriseId: ${enterpriseId}`);
  }

  const { data } = await coreAPI.get(
    `enterprise/${enterpriseId}/data-availability/years`
  );
  return data;
};

// Fetch total count of items needing action (unverified entities)
export const fetchActionNeededCount = async (
  enterpriseId: string
): Promise<{ count: number; processingFileCount: number }> => {
  if (
    !enterpriseId ||
    enterpriseId === "undefined" ||
    !/^\d+$/.test(enterpriseId)
  ) {
    throw new Error(`Invalid enterpriseId: ${enterpriseId}`);
  }

  const { data } = await coreAPI.get(
    `enterprise/${enterpriseId}/data-availability/action-needed-count`
  );
  return data;
};

// Data availability status type
export type DataAvailabilityStatus =
  | "available"
  | "partial"
  | "needs-input"
  | "processing"
  | "no-data"
  | "locked";

// Field data availability response types
export interface FieldDataAvailability {
  fieldId: number;
  fieldName: string;
  farmId: number;
  farmName: string;
  organisationId: number;
  organisationName: string;
  dataStatus: DataAvailabilityStatus;
  /** Per-entity status breakdown (e.g., { yield: 'available', fuel: 'no-data' }) */
  byEntity: Record<string, DataAvailabilityStatus>;
  boundaries: {
    type: "MultiPolygon";
    coordinates: any;
  };
}

export interface FieldDataAvailabilityResponse {
  fields: FieldDataAvailability[];
  year?: number;
}

// Fetch aggregated field-level data availability for map visualization
export const fetchFieldDataAvailability = async (
  enterpriseId: string,
  options?: {
    year?: number;
    farmIds?: number[];
    organisationIds?: number[];
  }
): Promise<FieldDataAvailabilityResponse> => {
  if (
    !enterpriseId ||
    enterpriseId === "undefined" ||
    enterpriseId === "null"
  ) {
    throw new Error(`Invalid enterpriseId: ${enterpriseId}`);
  }

  const searchParams = new URLSearchParams();
  if (options?.year) searchParams.set("year", options.year.toString());
  if (options?.farmIds && options.farmIds.length > 0) {
    options.farmIds.forEach((id) =>
      searchParams.append("farmIds", id.toString())
    );
  }
  if (options?.organisationIds && options.organisationIds.length > 0) {
    options.organisationIds.forEach((id) =>
      searchParams.append("organisationIds", id.toString())
    );
  }

  const queryString = searchParams.toString();
  const url = queryString
    ? `enterprise/${enterpriseId}/data-availability/field-polygons?${queryString}`
    : `enterprise/${enterpriseId}/data-availability/field-polygons`;

  const { data } = await coreAPI.get(url);
  return data;
};

// ==================================
// DATA REVIEW
// ==================================

export interface UnverifiedItem {
  entityType: string;
  entityId: number;
  data: Record<string, unknown>;
  planting?: {
    id: number;
    field?: {
      id: number;
      name: string;
      farm?: { id: number; name: string };
    };
    cropVariety?: {
      id: number;
      name: string;
      crop?: { id: number; name: string };
    };
  };
  /** Field object for field-based entities (e.g., soil) */
  field?: {
    id: number;
    name: string;
    farm?: { id: number; name: string };
  };
  /** True if this entity uses fieldId instead of plantingId (e.g., soil) */
  usesFieldId?: boolean;
  requiredFields: string[]; // Fields required for verification
  createdAt: string;
  rawCustomerDataId?: number;
}

export interface UnverifiedFarmGroup {
  id: number;
  name: string;
  items: UnverifiedItem[];
  totalItems?: number; // Total items for pagination
}

export interface UnverifiedResponse {
  farms: UnverifiedFarmGroup[];
  totalCount: number;
  countsByEntityType?: Record<string, number>; // Accurate counts per entity type
}

// Entity config types - required fields from VERIFICATION_REQUIRED_FIELDS
export interface EnumFieldOption {
  value: string;
  label: string;
}

export interface EntityTypeConfig {
  entityType: string;
  label: string;
  dateField: string; // Primary date field for this entity (e.g., "date", "dateOfInvoice")
  requiredFields: string[]; // Payload field names required for verification
  enumFields: Record<string, EnumFieldOption[]>; // Enum field options keyed by field name
  fieldLabels: Record<string, string>; // Localized field labels from API
}

export interface EntityConfigResponse {
  entities: EntityTypeConfig[];
}

export const fetchEntityConfig = async (
  enterpriseId: string
): Promise<EntityConfigResponse> => {
  const { data } = await coreAPI.get(
    `enterprise/${enterpriseId}/data-review/entity-config`
  );
  return data;
};

export const fetchUnverifiedEntities = async (
  enterpriseId: string,
  filters?: {
    entityType?: string;
    farmId?: number;
    organisationId?: number;
    pageSize?: number;
    page?: number;
    groupBy?: "organisation" | "farm";
  }
): Promise<UnverifiedResponse> => {
  const params = new URLSearchParams();
  params.append("groupBy", filters?.groupBy || "farm");

  if (filters?.entityType) {
    params.append("entityType", filters.entityType);
  }
  if (filters?.farmId) {
    params.append("farmId", String(filters.farmId));
  }
  if (filters?.organisationId) {
    params.append("organisationId", String(filters.organisationId));
  }
  if (filters?.pageSize !== undefined) {
    params.append("pageSize", String(filters.pageSize));
  }
  if (filters?.page !== undefined) {
    params.append("page", String(filters.page));
  }

  const { data } = await coreAPI.get(
    `enterprise/${enterpriseId}/data-review/unverified?${params.toString()}`
  );
  return data;
};

export interface SaveEntitiesPayload {
  entities: Array<{
    entityType: string;
    entityId: number;
    edits?: Record<string, unknown>;
  }>;
  verifyIfComplete?: boolean; // Default true. If false, just saves (draft mode)
}

export interface SaveEntitiesResponse {
  saved: number;
  verified: number;
  failed: number;
}

export const saveEntities = async (
  enterpriseId: string,
  payload: SaveEntitiesPayload
): Promise<SaveEntitiesResponse> => {
  const { data } = await coreAPI.patch(
    `enterprise/${enterpriseId}/data-review/save`,
    payload
  );
  return data;
};

// Planting options for dropdown selection
export interface PlantingOption {
  id: number;
  label: string;
  fieldId: number;
  fieldName: string;
  cropVariety?: string;
  year?: number;
}

export interface PlantingOptionsResponse {
  plantings: PlantingOption[];
}

export const fetchPlantingsForFarm = async (
  enterpriseId: string,
  farmId: number
): Promise<PlantingOptionsResponse> => {
  const { data } = await coreAPI.get(
    `enterprise/${enterpriseId}/data-review/farm/${farmId}/plantings`
  );
  return data;
};

// Field options for dropdown selection (used by field-based entities like soil)
export interface FieldOption {
  id: number;
  name: string;
  farmId: number;
  farmName: string;
}

export interface FieldOptionsResponse {
  fields: FieldOption[];
}

export const fetchFieldsForFarm = async (
  enterpriseId: string,
  farmId: number
): Promise<FieldOptionsResponse> => {
  const { data } = await coreAPI.get(
    `enterprise/${enterpriseId}/data-review/farm/${farmId}/fields`
  );
  return data;
};

// Crop input product options for dropdown selection
export interface CropInputProductOption {
  id: number;
  productLabel: string;
  manufacturer: string | null;
  cropInputCategoryEnum: string | null;
}

export const fetchCropInputProducts = async (
  enterpriseId: string,
  search?: string,
  limit = 50
): Promise<CropInputProductOption[]> => {
  const params = new URLSearchParams();
  if (search) params.append("q", search);
  params.append("limit", String(limit));

  const { data } = await coreAPI.get(
    `enterprises/${enterpriseId}/data-points/crop-input-products/search?${params}`
  );
  return data;
};

export const fetchCropInputProductById = async (
  enterpriseId: string,
  productId: number
): Promise<CropInputProductOption[]> => {
  const { data } = await coreAPI.get(
    `enterprises/${enterpriseId}/data-points/crop-input-products/search?id=${productId}`
  );
  return data;
};

export const fetchRawCustomerProjectDataData = async (
  projectTaskId: string
) => {
  const { data } = await coreAPI.get(`raw-data/project-task/${projectTaskId}`);
  return data;
};

export const fetchRawCustomerYieldData = async (enterpriseId: string) => {
  const encodedDataTag = encodeURIComponent("Yield data");
  const { data } = await coreAPI.get(
    `raw-data/enterprise/${enterpriseId}/data-tag/${encodedDataTag}`
  );
  return data;
};

export const fetchRawCustomerLandUseYieldData = async (
  enterpriseId: string
) => {
  const encodedDataTag = encodeURIComponent("Land use 1 Credit");
  const { data } = await coreAPI.get(
    `raw-data/enterprise/${enterpriseId}/data-tag/${encodedDataTag}`
  );
  return data;
};

export const fetchRawCustomerSoilOldData = async (enterpriseId: string) => {
  const encodedDataTag = encodeURIComponent("Soil Credit");
  const { data } = await coreAPI.get(
    `raw-data/enterprise/${enterpriseId}/data-tag/${encodedDataTag}`
  );
  return data;
};

export const fetchRawCustomerSoilData = async (enterpriseId: string) => {
  const encodedDataTag = encodeURIComponent("Soil data");
  const { data } = await coreAPI.get(
    `raw-data/enterprise/${enterpriseId}/data-tag/${encodedDataTag}`
  );
  return data;
};

export const fetchRawCustomerLandManagementSprayData = async (
  enterpriseId: string
) => {
  const encodedDataTag = encodeURIComponent("Land management 1 Credit");
  const { data } = await coreAPI.get(
    `raw-data/enterprise/${enterpriseId}/data-tag/${encodedDataTag}`
  );
  return data;
};

export const fetchRawCustomerSprayData = async (enterpriseId: string) => {
  const encodedDataTag = encodeURIComponent("Spray data");
  const { data } = await coreAPI.get(
    `raw-data/enterprise/${enterpriseId}/data-tag/${encodedDataTag}`
  );
  return data;
};

/**
 * Fetch compliance documents (SOPs, policies, certifications, etc.) for an enterprise.
 * These are audit evidence documents tagged with COMPLIANCE_DOCS.
 */
export const fetchComplianceDocs = async (enterpriseId: string) => {
  const encodedDataTag = encodeURIComponent("COMPLIANCE_DOCS");
  const { data } = await coreAPI.get(
    `raw-data/enterprise/${enterpriseId}/data-tag/${encodedDataTag}`
  );
  return data;
};

/**
 * Fetch the Audit Hub's polished compliance-documents view for an enterprise:
 * uploader (or integration name for automated imports), expiry date/source +
 * derived status, and audit-usage count. See ComplianceDocumentEntity in
 * core-api (ENG-1537).
 */
export const fetchComplianceDocuments = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(
    `raw-data/enterprise/${enterpriseId}/compliance-documents`
  );
  return data;
};

/**
 * Set or clear a compliance document's expiry date (ENG-1537). Passing null
 * clears it. Marks the date as USER_SET so the pipeline/backfill never
 * overwrites a manual correction.
 */
export const updateComplianceDocExpiry = async (
  rawDataId: number,
  expiryDate: string | null
) => {
  const { data } = await coreAPI.patch(`raw-data/${rawDataId}/expiry`, {
    expiryDate,
  });
  return data;
};

export const fetchRawCustomerPrePropData = async (
  prePropPayload: PrePropPayloadType
) => {
  const { data } = await coreAPI.post(
    `raw-data/enterprise/${prePropPayload.enterpriseId}/get-prepop-csv/`,
    prePropPayload
  );
  return data;
};

export const postRawCustomerData = async (
  formData: FormData,
  enterpriseId: string
): Promise<RawCustomerDataType> => {
  const { data } = await coreAPI.post(
    `/raw-data/enterprise/${enterpriseId}/customer-file`,
    formData
  );
  return data;
};

export type TriggerUnstractPayload = {
  enterprise_id: number;
  user_id: number;
  ingest_files: Array<{
    uri: string;
    raw_customer_data_id?: number;
    entities?: string[]; // e.g., ["audit_templates", "crop_input"]
  }>;
  organisation_id?: number;
  farm_id?: number;
  project_id?: number;
  field_id?: number;
  additional_instructions?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
};

export const triggerUnstractEtl = async (
  payload: TriggerUnstractPayload,
  options?: { requestId?: string; signal?: AbortSignal }
) => {
  const config = {
    headers: options?.requestId
      ? { "Idempotency-Key": options.requestId }
      : undefined,
    signal: options?.signal,
  } as any;
  const { data } = await coreAPI.post(
    `/etl-pipelines/trigger`,
    payload,
    config
  );

  return data;
};

export const deleteItem = async (itemId: string) => {
  const { data } = await coreAPI.delete(`/raw-data/${itemId}`);
  return data;
};

// WARNING: this deletes all raw customer data for the enterprise

export const deleteEnterpriseRawCustomerData = async (enterpriseId: string) => {
  const { data } = await coreAPI.delete(`/raw-data/enterprise/${enterpriseId}`);
  return data;
};

export const createPublicSheet = async (
  enterpriseId: string,
  projectTaskId: string
) => {
  const { data } = await coreAPI.post(
    `/raw-data/enterprise/${enterpriseId}/create-public-sheet/${projectTaskId}`
  );
  return data;
};

// ==================================
// SIGNED URL UPLOAD (Direct browser-to-GCS upload)
// ==================================

export type CreateRawDataForUploadParams = {
  enterpriseId: number;
  organisationId?: number;
  farmId?: number;
  fieldId?: number;
  fileName: string;
  contentType: string;
  fileSize: number;
  tags?: string[];
  projectTaskId?: number;
  contentHash?: string;
  sourceType?: "GOOGLE_DRIVE" | "ONEDRIVE" | "MANUAL" | "SCRAPER";
};

export type CreateRawDataForUploadResponse = {
  rawCustomerDataId: number;
  signedUploadUrl: string;
  gcsPath: string;
  gsUri: string;
};

export type ConfirmUploadResponse = {
  success: boolean;
  batchId?: string;
};

/**
 * Creates a RawCustomerData record and returns a signed URL for direct upload to GCS.
 * Step 1 of 3 in the signed URL upload flow.
 */
export const createRawDataForUpload = async (
  params: CreateRawDataForUploadParams
): Promise<CreateRawDataForUploadResponse> => {
  const { enterpriseId, ...body } = params;
  const { data } = await coreAPI.post(
    `/raw-data/enterprise/${enterpriseId}/create`,
    body
  );
  return data;
};

/**
 * Uploads a file directly to GCS using a signed URL.
 * Step 2 of 3 in the signed URL upload flow.
 * Note: This bypasses the core-api entirely - the browser uploads directly to GCS.
 */
export const uploadToSignedUrl = async (
  signedUrl: string,
  file: File
): Promise<void> => {
  const response = await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
};

/**
 * Confirms that a file has been uploaded to GCS and triggers the ETL pipeline.
 * Step 3 of 3 in the signed URL upload flow.
 */
export const confirmRawDataUpload = async (
  rawCustomerDataId: number
): Promise<ConfirmUploadResponse> => {
  const { data } = await coreAPI.post(`/raw-data/${rawCustomerDataId}/confirm`);
  return data;
};

// ==================================
// SCORE
// ==================================

export const fetchScoreData = async (farmId: string) => {
  const { data } = await coreAPI.get(`/score/${farmId}`);
  return data;
};

export const fetchOrganisationScoreData = async (organisationId: string) => {
  const { data } = await coreAPI.get(`score/organisation/${organisationId}`);
  return data;
};

export const fetchEnterpriseScoreData = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(`score/enterprise/${enterpriseId}`);
  return data;
};

export const fetchEnterpriseOrganisationScoresData = async (
  enterpriseId: string
) => {
  const { data } = await coreAPI.get(
    `score/enterpriseOrganisations/${enterpriseId}`
  );
  return data;
};
export const fetchEnterpriseFarmScoresData = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(`score/enterpriseFarms/${enterpriseId}`);
  return data;
};

export const fetchOrganisationFarmScoresData = async (
  organisationId: string
) => {
  const { data } = await coreAPI.get(
    `score/organisationFarms/${organisationId}`
  );
  return data;
};

// ==================================
// PROJECTS
// ==================================

export const fetchEnterpriseProjects = async (
  enterpriseId: string,
  projectType?: ProjectTypeType
) => {
  const { data } = await coreAPI.get(
    `enterprises/${enterpriseId}/projects${
      projectType ? `?projectTypeType=${projectType}` : ""
    }`
  );
  return data;
};

export const fetchProject = async (enterpriseId: string, projectId: string) => {
  const { data } = await coreAPI.get(
    `enterprises/${enterpriseId}/projects/${projectId}`
  );

  return data;
};

export const fetchProjectEntitiesStatusSummary = async (
  enterpriseId: string,
  projectId: string,
  farmIds?: string[]
) => {
  const params = new URLSearchParams();
  if (farmIds && farmIds.length > 0) {
    farmIds.forEach((id) => params.append("farmIds", id));
  }
  const queryString = params.toString();
  const url = `enterprises/${enterpriseId}/data-points/projects/${projectId}/entity-status-summary${
    queryString ? `?${queryString}` : ""
  }`;
  const { data } = await coreAPI.get(url);
  return data;
};

export const fetchProjectStatusesSummary = async (
  enterpriseId: string,
  projectId: string,
  farmId?: string
) => {
  const { data } = await coreAPI.get(
    `enterprises/${enterpriseId}/data-points/projects/${projectId}/entity-status-summary-by-status?farmId=${farmId}`
  );
  return data;
};

export const toggleProjectTaskStatus = async ({
  enterpriseId,
  projectId,
  taskId,
}: {
  enterpriseId: string;
  projectId: number;
  taskId: number;
}) => {
  const { data } = await coreAPI.patch(
    `enterprise/${enterpriseId}/project/${projectId}/project-tasks/${taskId}/toggle-status`
  );
  return data;
};

export const completeProjectTask = async ({
  enterpriseId,
  projectId,
  taskId,
}: {
  enterpriseId: string;
  projectId: number;
  taskId: number;
}) => {
  const { data } = await coreAPI.patch(
    `enterprise/${enterpriseId}/project/${projectId}/project-tasks/${taskId}/mark-as-completed`
  );
  return data;
};

export const inProgressProjectTask = async ({
  enterpriseId,
  projectId,
  taskId,
}: {
  enterpriseId: string;
  projectId: number;
  taskId: number;
}) => {
  const { data } = await coreAPI.patch(
    `enterprise/${enterpriseId}/project/${projectId}/project-tasks/${taskId}/mark-as-in-progress`
  );
  return data;
};

export const notStartedProjectTask = async ({
  enterpriseId,
  projectId,
  taskId,
}: {
  enterpriseId: string;
  projectId: number;
  taskId: number;
}) => {
  const { data } = await coreAPI.patch(
    `enterprise/${enterpriseId}/project/${projectId}/project-tasks/${taskId}/mark-as-not-started`
  );
  return data;
};

// TODO: update to use name instead of ID
export const createProject = async (
  createProjectPayload: CreateProjectPayload
) => {
  const { data } = await coreAPI.post(
    `/enterprises/${createProjectPayload.enterpriseId}/projects`,
    createProjectPayload
  );
  return data;
};

export const updateProject = async ({
  enterpriseId,
  projectId,
  name,
}: {
  enterpriseId: string;
  projectId: number;
  name: string;
}) => {
  const { data } = await coreAPI.patch(
    `/enterprises/${enterpriseId}/projects/${projectId}`,
    { name }
  );
  return data;
};

export const fetchProjectTypes = async (category: string) => {
  const { data } = await coreAPI.get(`/project-types/${category}`);
  return data;
};

export const deleteAllProjectsByEnterpriseId = async (enterpriseId: string) => {
  const { data } = await coreAPI.delete(
    `/enterprises/${enterpriseId}/projects`
  );
  return data;
};

export const updateTaskStatus = async ({
  enterpriseId,
  projectId,
  taskId,
  status,
}: {
  enterpriseId: string;
  projectId: number;
  taskId: number;
  status: TaskStatus;
}) => {
  const { data } = await coreAPI.patch(
    `/enterprise/${enterpriseId}/project/${projectId}/project-tasks/${taskId}/update-status`,
    { status }
  );

  return data;
};

// ==================================
// TASK STEP RESPONSES
// ==================================

// Get single task step response

export const fetchSingleTaskStepResponse = async (
  projectId: string,
  taskId: string,
  stepId: string,
  responseId: string
) => {
  const { data } = await coreAPI.get(
    `projects/${projectId}/tasks/${taskId}/steps/${stepId}/responses/${responseId}`
  );

  return data;
};

export const fetchTaskResponses = async (projectId: string, taskId: string) => {
  const { data } = await coreAPI.get(
    `projects/${projectId}/tasks/${taskId}/responses`
  );
  return data;
};

// Get single task step response

export const updateTaskStepResponse = async (
  projectId: string,
  taskId: string,
  stepId: string,
  responseId: string,
  updateTaskStepResponseDto: UpdateTaskStepPayload
) => {
  const { data } = await coreAPI.patch(
    `projects/${projectId}/tasks/${taskId}/steps/${stepId}/responses/${responseId}`,
    updateTaskStepResponseDto
  );
  return data;
};

// ==================================
// VALUE DRIVERS
// ==================================

export const getValueDrivers = async () => {
  const { data } = await coreAPI.get(`/value-drivers`);
  return data;
};

export const getEnterpriseValueDrivers = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(
    `/value-drivers/enterprise/${enterpriseId}`
  );
  return data;
};

export const createValueDriver = async (
  enterpriseId: string,
  createEnterpriseValueDriver: CreateEnterpriseValueDriver
) => {
  const { data } = await coreAPI.put(
    `/value-drivers/enterprise/${enterpriseId}`,
    createEnterpriseValueDriver
  );
  return data;
};

// ==================================
// QUESTIONS
// ==================================
export const fetchQuestions = async () => {
  const { data } = await coreAPI.get(`/questions`);
  return data;
};

export const fetchSingleQuestion = async (questionId: string) => {
  const { data } = await coreAPI.get(`/questions/${questionId}`);
  return data;
};

export const createQuestionResponse = async (
  enterpriseId: string,
  questionId: string,
  createQuestionResponseDto: QuestionResponsePayload
) => {
  const { data } = await coreAPI.put(
    `/questions/${questionId}/enterprise/${enterpriseId}/response`,
    createQuestionResponseDto
  );
  return data;
};

// ==================================
// GEO DATA
// ==================================

interface GeoQueryParams {
  organisationId?: number;
  farmId?: number;
  fieldId?: number;
  enrichYield?: boolean;
  enrichCropInputs?: boolean;
  enrichWater?: boolean;
  cropInputCategory?: string;
  years?: number[];
  cropIds?: number | number[];
  cropVarietyIds?: number[];
  farmIds?: number[];
}

const buildGeoQueryParams = (params?: GeoQueryParams): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (params?.organisationId) {
    searchParams.append("organisationId", String(params.organisationId));
  }
  if (params?.farmId) {
    searchParams.append("farmId", String(params.farmId));
  }
  if (params?.fieldId) {
    searchParams.append("fieldId", String(params.fieldId));
  }

  if (params?.enrichYield) {
    searchParams.append("enrichYield", "true");
  }
  if (params?.enrichCropInputs) {
    searchParams.append("enrichCropInputs", "true");
  }
  if (params?.enrichWater) {
    searchParams.append("enrichWater", "true");
  }
  if (params?.cropInputCategory) {
    searchParams.append("cropInputCategory", params.cropInputCategory);
  }

  if (Array.isArray(params?.years) && params?.years.length) {
    params.years.forEach((y) => searchParams.append("years", String(y)));
  }

  if (Array.isArray(params?.cropIds) && params?.cropIds.length) {
    params.cropIds.forEach((id) => searchParams.append("cropIds", String(id)));
  } else if (typeof params?.cropIds === "number") {
    searchParams.append("cropIds", String(params.cropIds));
  }

  if (Array.isArray(params?.cropVarietyIds) && params?.cropVarietyIds.length) {
    params.cropVarietyIds.forEach((id) =>
      searchParams.append("cropVarietyIds", String(id))
    );
  }

  if (Array.isArray(params?.farmIds) && params?.farmIds.length) {
    params.farmIds.forEach((id) => searchParams.append("farmIds", String(id)));
  }

  return searchParams;
};

export const getEnterpriseCentroids = async (
  enterpriseId: string,
  params?: GeoQueryParams
) => {
  const searchParams = buildGeoQueryParams(params);
  const queryString = searchParams.toString();
  const url = `/enterprises/${enterpriseId}/fields/centroids${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await coreAPI.get(url);
  return data;
};

export const getEnterprisePolygons = async (
  enterpriseId: string,
  params?: GeoQueryParams
) => {
  const searchParams = buildGeoQueryParams(params);
  const queryString = searchParams.toString();
  const url = `/enterprises/${enterpriseId}/fields/polygons${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await coreAPI.get(url);
  return data;
};

// ===============================
// Review Yields
// ===============================
export const fetchReviewData = async (
  entityType?: ModalEntityType,
  reviewed?: boolean,
  page?: number,
  limit?: number,
  projectId?: number
) => {
  const params = new URLSearchParams();
  if (reviewed !== undefined) {
    params.append("reviewed", reviewed.toString());
  }
  if (page !== undefined) {
    params.append("page", page.toString());
  }
  if (limit !== undefined) {
    params.append("limit", limit.toString());
  }
  if (entityType !== undefined) {
    params.append("entityType", toUpperSnakeCase(entityType));
  }
  if (projectId !== undefined) {
    params.append("projectId", projectId.toString());
  }
  const { data } = await coreAPI.get(`/review?${params.toString()}`);
  return data;
};

// ==================================
// UNITS
// ==================================
export const fetchAllUnits = async () => {
  const endpoint = `/units`;
  const response = await coreAPI.get(endpoint);
  return response.data;
};

// ==================================
// DATA POINTS ENDPOINTS
// ==================================
export type DataPointMetaFilters = {
  onlyWithPlantings?: boolean;
  date?: string;
  farmId?: number;
  fieldId?: number;
};

export const fetchDataPointMeta = async (
  enterpriseId: string,
  type: ModalEntityType,
  filters: DataPointMetaFilters = {}
) => {
  const searchParams = new URLSearchParams();
  // Normalise entity type to match backend expectations
  searchParams.set("type", toUpperSnakeCase(type));

  if (filters.onlyWithPlantings) {
    searchParams.append("onlyWithPlantings", "true");
  }

  if (filters.date) {
    searchParams.append("date", filters.date);
  }

  if (typeof filters.farmId === "number") {
    searchParams.append("farmId", String(filters.farmId));
  }

  if (typeof filters.fieldId === "number") {
    searchParams.append("fieldId", String(filters.fieldId));
  }

  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/data-points/meta-data?${searchParams.toString()}`
  );
  return data;
};

export const fetchUnifiedDataPointsTable = async (
  enterpriseId: string,
  type: ModalEntityType,
  options?: { includeHidden?: boolean; light?: boolean }
) => {
  const searchParams = new URLSearchParams();
  // Normalise entity type to match backend expectations
  searchParams.set("type", toUpperSnakeCase(type));
  // default light=true to preserve previous behavior
  const light = options?.light ?? true;
  if (light) searchParams.set("light", "true");
  if (options?.includeHidden) searchParams.set("includeHidden", "true");

  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/data-points/unified-table?${searchParams.toString()}`
  );
  return data;
};

/**
 * Verify an entity record (Yield, Fuel, CropInput, etc.)
 * Sets verified_at, verified_by_id, and verification_method='MANUAL'
 * Used when users review and approve unverified data points
 */
export const verifyDataPoint = async (
  enterpriseId: string,
  entityType: string,
  recordId: number
): Promise<{
  id: number;
  verifiedAt: string;
  verifiedById: number | null;
  verificationMethod: string;
}> => {
  // Normalise entity type to UPPER_SNAKE_CASE
  const normalizedType = entityType.toUpperCase().replace(/-/g, "_");
  const response = await coreAPI.patch(
    `/enterprises/${enterpriseId}/data-points/${normalizedType}/${recordId}/verify`
  );
  return response.data;
};

/**
 * Update an entity record (Yield, Fuel, CropInput, Water, etc.)
 * Updates the specified fields and optionally verifies the record in the same request
 */
export const updateDataPoint = async (
  enterpriseId: string,
  entityType: string,
  recordId: number,
  updates: Record<string, unknown>,
  verify?: boolean
): Promise<{
  id: number;
  verifiedAt?: string;
  verifiedById?: number | null;
  verificationMethod?: string;
}> => {
  const normalizedType = entityType.toUpperCase().replace(/-/g, "_");
  const response = await coreAPI.patch(
    `/enterprises/${enterpriseId}/data-points/${normalizedType}/${recordId}`,
    { updates, verify }
  );
  return response.data;
};

/**
 * Delete an entity record (Yield, Fuel, CropInput, Water, etc.)
 */
export const deleteDataPoint = async (
  enterpriseId: string,
  entityType: string,
  recordId: number
): Promise<{ success: boolean; id: number }> => {
  const normalizedType = entityType.toUpperCase().replace(/-/g, "_");
  const response = await coreAPI.delete(
    `/enterprises/${enterpriseId}/data-points/${normalizedType}/${recordId}`
  );
  return response.data;
};

/**
 * Bulk update multiple entity records
 * Updates the specified fields on all provided records and optionally verifies them
 */
export const bulkUpdateDataPoints = async (
  enterpriseId: string,
  items: Array<{ entityType: string; recordId: number }>,
  updates: Record<string, unknown>,
  verify?: boolean
): Promise<{
  updated: number;
  failed: Array<{ recordId: number; error: string }>;
}> => {
  const normalizedItems = items.map((item) => ({
    entityType: item.entityType.toUpperCase().replace(/-/g, "_"),
    recordId: item.recordId,
  }));
  const response = await coreAPI.patch(
    `/enterprises/${enterpriseId}/data-points/bulk`,
    { items: normalizedItems, updates, verify }
  );
  return response.data;
};

/**
 * Fetch field configuration for all entity types
 * Returns required and editable fields for each entity type
 */
export interface EntityFieldConfig {
  requiredFields: string[];
  editableFields: string[];
  bulkEditableFields: string[];
  fieldLabels: Record<string, string>;
}

export const fetchFieldConfig = async (
  enterpriseId: string
): Promise<Record<string, EntityFieldConfig>> => {
  const response = await coreAPI.get(
    `/enterprises/${enterpriseId}/data-points/field-config`
  );
  return response.data;
};

/**
 * Bulk create data request items for a project
 * Used when creating a new carbon footprint project to generate all required data collection items
 */
export interface BulkCreateDataRequestItemPayload {
  type: ReviewEntityType;
  subCategory?: DataRequestSubCategory;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  scopeType: "ORGANISATION" | "FARM" | "FIELD";
  scopeId: number;
  cropId?: number;
  cropVarietyId?: number;
  projectId?: number;
}

export const bulkCreateDataRequestItems = async (
  enterpriseId: string,
  payload: BulkCreateDataRequestItemPayload
): Promise<any> => {
  const response = await coreAPI.post(
    `/enterprises/${enterpriseId}/data-points/create-data-request-items`,
    payload
  );
  return response.data;
};

/**
 * Create footprint project with all data request items atomically
 * This replaces the need for multiple bulkCreateDataRequestItems calls
 */
export interface CreateFootprintProjectWithDataItemsPayload {
  name: string;
  projectScope?: BackendProjectScope;
  farmIds: number[];
  cropVarieties: Array<{
    cropId: number;
    varietyIds: number[];
  }>;
  startDate: string;
  endDate: string;
}

export const createFootprintProjectWithDataItems = async (
  enterpriseId: string,
  payload: CreateFootprintProjectWithDataItemsPayload
): Promise<{
  project: any;
  jobId: string;
  message: string;
}> => {
  const response = await coreAPI.post(
    `/enterprises/${enterpriseId}/projects/footprint/with-data-items`,
    payload
  );
  return response.data;
};

/**
 * Get status of a project data items creation job
 */
export interface ProjectDataItemsJobStatus {
  jobId: string;
  state:
    | "created"
    | "retry"
    | "active"
    | "completed"
    | "expired"
    | "cancelled"
    | "failed";
  completedOn: string | null;
  output: {
    projectId: number;
    totalDataItemsCreated: number;
    totalSkipped: number;
    totalDuplicates: number;
    totalFailed: number;
  } | null;
}

export const getProjectJobStatus = async (
  enterpriseId: string,
  jobId: string
): Promise<ProjectDataItemsJobStatus> => {
  const response = await coreAPI.get(
    `/enterprises/${enterpriseId}/projects/jobs/${jobId}/status`
  );
  return response.data;
};

export const fetchProjectDataRequestItems = async (
  enterpriseId: string,
  projectId: string
): Promise<{
  counts: {
    NOT_STARTED: number;
    IN_PROGRESS: number;
    IN_REVIEW: number;
    COMPLETED: number;
    [key: string]: number;
  };
  total: number;
  completedWithFinal: number;
  completedWithoutFinal: number;
  percentageComplete: number;
}> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/data-points/projects/${projectId}/data-request-items`
  );
  return data;
};

export const fetchDataStatusSummaryByStatus = async (
  enterpriseId: string,
  organisationId?: string,
  farmId?: string,
  options?: { includeHidden?: boolean; farmIds?: string[]; fieldIds?: string[] }
) => {
  const params = new URLSearchParams();
  if (organisationId) params.set("organisationId", organisationId);
  if (farmId) params.set("farmId", farmId);
  if (options?.includeHidden) params.set("includeHidden", "true");

  // Support multiple farm IDs
  if (options?.farmIds && options.farmIds.length > 0) {
    options.farmIds.forEach((id) => params.append("farmIds", id));
  }

  // Support multiple field IDs
  if (options?.fieldIds && options.fieldIds.length > 0) {
    options.fieldIds.forEach((id) => params.append("fieldIds", id));
  }

  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/data-points/entity-status-summary-by-status?${params.toString()}`
  );
  return data;
};

// Fetch the per-entity status summary for a given date range.
// Backwards compatible: if called with a year string only, derive start/end for that year.
export const fetchEntityStatusSummary = async (
  enterpriseId: string,
  yearOrStartDate: string,
  endDate?: string,
  options?: { includeHidden?: boolean }
) => {
  let start = yearOrStartDate;
  let end = endDate;

  // If only a year was provided (e.g., "2024"), compute full-year range in UTC
  if (!end) {
    const year = String(yearOrStartDate).trim();
    if (/^\d{4}$/.test(year)) {
      start = `${year}-01-01T00:00:00.000Z`;
      end = `${year}-12-31T23:59:59.999Z`;
    }
  }

  const params = new URLSearchParams();
  if (start) params.set("startDate", String(start));
  if (end) params.set("endDate", String(end));
  if (options?.includeHidden) params.set("includeHidden", "true");

  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/data-points/entity-status-summary?${params.toString()}`
  );
  return data;
};

// Batch fetch entity status summaries for multiple year ranges in a single request
export const fetchEntityStatusSummaryBatch = async (
  enterpriseId: string,
  yearRanges: Array<{ startDate: string; endDate: string; label: string }>,
  options?: {
    includeHidden?: boolean;
    farmIds?: string[];
    fieldIds?: string[];
    projectIds?: string[];
  }
): Promise<Record<string, any>> => {
  const params = new URLSearchParams();
  if (options?.includeHidden) params.set("includeHidden", "true");

  // Build request body with yearRanges and optional filters
  const requestBody: {
    yearRanges: Array<{ startDate: string; endDate: string; label: string }>;
    farmIds?: number[];
    fieldIds?: number[];
    projectIds?: number[];
  } = { yearRanges };

  // Convert string IDs to numbers for the API, filtering out invalid values
  if (options?.farmIds && options.farmIds.length > 0) {
    requestBody.farmIds = options.farmIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }
  if (options?.fieldIds && options.fieldIds.length > 0) {
    requestBody.fieldIds = options.fieldIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }
  if (options?.projectIds && options.projectIds.length > 0) {
    requestBody.projectIds = options.projectIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }

  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/data-points/entity-status-summary-batch?${params.toString()}`,
    requestBody
  );

  return data.results || data;
};

// Fetch entity status summary with auto-determined year range from backend
export const fetchEntityStatusSummaryAuto = async (
  enterpriseId: string,
  options?: {
    includeHidden?: boolean;
    farmIds?: string[];
    fieldIds?: string[];
    projectIds?: string[];
  }
): Promise<{
  yearRange: { min: string; max: string };
  data: Record<string, Record<string, string>>;
}> => {
  const params = new URLSearchParams();
  if (options?.includeHidden) params.set("includeHidden", "true");

  // Use array bracket notation for array params (e.g., farmIds[]=1&farmIds[]=2)
  if (options?.farmIds && options.farmIds.length > 0) {
    options.farmIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
      .forEach((id) => params.append("farmIds[]", id.toString()));
  }
  if (options?.fieldIds && options.fieldIds.length > 0) {
    options.fieldIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
      .forEach((id) => params.append("fieldIds[]", id.toString()));
  }
  if (options?.projectIds && options.projectIds.length > 0) {
    options.projectIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
      .forEach((id) => params.append("projectIds[]", id.toString()));
  }

  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/data-points/entity-status-summary?${params.toString()}`
  );

  return data;
};

// Search crop input products by product label (server-side search)
export interface CropInputProductSearchResult {
  id: number;
  productLabel: string;
  manufacturer: string | null;
  cropInputCategoryEnum: string | null;
}

export const searchCropInputProducts = async (
  enterpriseId: string,
  query: string,
  productId?: number,
  limit: number = 20
): Promise<CropInputProductSearchResult[]> => {
  const params = new URLSearchParams();

  // If productId is provided, fetch by ID instead of search
  if (productId) {
    params.set("id", String(productId));
  } else {
    if (!query || query.trim().length < 2) {
      return [];
    }
    params.set("q", query.trim());
  }
  params.set("limit", String(limit));

  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/data-points/crop-input-products/search?${params.toString()}`
  );
  return data;
};

// ==================================
// COMMENTS (Thread-based, generic)
// ==================================

export const fetchThreadComments = async (threadId: number) => {
  const { data } = await coreAPI.get(`/comments/thread/${threadId}`);
  return data;
};

export const createComment = async (
  dto: CreateCommentDto
): Promise<CreateCommentResponse> => {
  const { data } = await coreAPI.post(`/comments`, dto);
  return data;
};

export const editComment = async (dto: EditCommentDto) => {
  const { data } = await coreAPI.patch(`/comments/${dto.id}`, {
    authorId: dto.authorId,
    content: dto.content,
    // Only sent when the caller is actually changing them — the backend
    // leaves each field untouched when it's absent.
    ...(dto.entityType !== undefined && { entityType: dto.entityType }),
    ...(dto.priority !== undefined && { priority: dto.priority }),
  });
  return data;
};

export const deleteComment = async (dto: DeleteCommentDto) => {
  const { data } = await coreAPI.patch(`/comments/delete/${dto.id}`, {
    authorId: dto.authorId,
  });
  return data;
};

export const fetchCommentsByEntity = async (
  entityType: string,
  entityId: number
): Promise<ThreadCommentsFromAPI[]> => {
  const { data } = await coreAPI.get(
    `/comments/thread/entity/${entityType}/${entityId}`
  );
  return data;
};

export const fetchProjectComments = async (
  enterpriseId: number,
  projectId: number
): Promise<ThreadCommentsFromAPI[]> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/projects/${projectId}/comments`
  );
  return data;
};

export const createProjectComment = async (
  enterpriseId: number,
  projectId: number,
  dto: Omit<CreateCommentDto, "projectId">
): Promise<CreateCommentResponse> => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/projects/${projectId}/comments`,
    dto
  );
  return data;
};

export const resolveProjectComment = async (
  enterpriseId: number,
  projectId: number,
  commentId: number
): Promise<ThreadCommentsFromAPI> => {
  const { data } = await coreAPI.patch(
    `/enterprises/${enterpriseId}/projects/${projectId}/comments/${commentId}/resolve`
  );
  return data;
};

export const fetchProjectActivity = async (
  enterpriseId: number,
  projectId: number
): Promise<ProjectActivityEntry[]> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/projects/${projectId}/activity`
  );
  return data;
};

// ==================================
// FARM INSIGHTS
// ==================================

export interface FarmInsight {
  title: string;
  description: string;
  score: number;
  entities: string[];
  versionNumber: number;
  approved: boolean;
  approvedAt: string | null;
  createdAt: string;
  farms: string[];
  scope: string[];
}

export const fetchFarmInsights = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/farm-insights`
  );

  // Sort by score descending, then createdAt descending for stable ordering
  const sorted = Array.isArray(data)
    ? [...data].sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        // Fallback: newer insights first
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      })
    : data;
  return sorted;
};

// ==================================
// WEATHER
// ==================================

export interface WeatherData {
  current: {
    temp: number;
    weather: Array<{
      main: string;
      description: string;
      icon: string;
    }>;
  };
  daily: Array<{
    dt: number;
    temp: {
      min: number;
      max: number;
    };
    weather: Array<{
      main: string;
      description: string;
      icon: string;
    }>;
  }>;
}

export const fetchWeatherData = async (
  lat: number,
  lon: number,
  units: "imperial" | "metric" = "imperial"
): Promise<WeatherData> => {
  const apiKey = process.env.NEXT_PUBLIC_OPENWEATHER_API_KEY;
  const response = await fetch(
    `https://api.openweathermap.org/data/3.0/onecall?lat=${lat}&lon=${lon}&appid=${apiKey}&units=${units}&exclude=minutely,hourly,alerts`
  );

  if (!response.ok) {
    throw new Error("Failed to fetch weather data");
  }

  return response.json();
};

// Interface for location data with country code
interface LocationData {
  name: string;
  country: string;
}

// Reverse geocoding to get location name and country from coordinates
export const fetchLocationData = async (
  lat: number,
  lon: number
): Promise<LocationData> => {
  const apiKey = process.env.NEXT_PUBLIC_OPENWEATHER_API_KEY;
  try {
    const response = await fetch(
      `https://api.openweathermap.org/geo/1.0/reverse?lat=${lat}&lon=${lon}&limit=1&appid=${apiKey}`
    );

    if (!response.ok) {
      return { name: "Your location", country: "US" }; // Default fallback
    }

    const data = await response.json();
    if (data.length > 0) {
      const location = data[0];

      return {
        name: location.name,
        country: location.country,
      };
    }

    return { name: "Your location", country: "US" }; // Default fallback
  } catch (error) {
    console.warn("Failed to fetch location data:", error);
    return { name: "Your location", country: "US" }; // Default fallback
  }
};

// Auto-detect units based on country code
export const getUnitsFromCountry = (
  countryCode: string
): "imperial" | "metric" => {
  // Countries that use Imperial system
  const IMPERIAL_COUNTRIES = ["US", "LR", "MM"]; // USA, Liberia, Myanmar
  const IMPERIAL_TERRITORIES = ["PR", "VI", "GU", "AS"]; // US territories

  return IMPERIAL_COUNTRIES.includes(countryCode) ||
    IMPERIAL_TERRITORIES.includes(countryCode)
    ? "imperial"
    : "metric";
};

// Weather forecast types for the widget
export interface WeatherForecastDay {
  date: string;
  isToday: boolean;
  temp: { min: number; max: number; unit: string };
  humidity: { value: number; unit: string };
  wind: { value: number; unit: string };
  frost: { value: number; unit: string };
  rain: { value: number; unit: string };
  precipitation: { value: number; unit: string };
}

export interface WeatherForecastMetrics {
  degreeDays: {
    value: number;
    baseTemp: number;
    dailyAvgTemps: number[];
    tempUnit: string; // '°F' or '°C'
  };
  chillHours: {
    value: number;
    rangeLow: number;
    rangeHigh: number;
    dailyMinTemps: number[];
    tempUnit: string; // '°F' or '°C'
  };
  soilMoistureDeficit: {
    value: number;
    et0: number;
    precipitation: number;
    unit: string;
  };
  sunburnRisk: {
    level: string;
    maxTemp: number;
    thresholdModerate: number | null;
    thresholdHigh: number | null;
    tempUnit: string; // '°F' or '°C'
  };
  diseaseRisk: {
    level: string;
    avgHumidity: number;
    humidityThreshold: number | null;
    totalPrecip: number;
    precipThreshold: number | null;
    precipUnit: string; // 'in' or 'mm'
  };
}

export interface WeatherForecastResponse {
  days: WeatherForecastDay[];
  metrics: WeatherForecastMetrics;
}

export interface CropThresholds {
  gddBaseTempF: number;
  chillTempLowF: number;
  chillTempHighF: number;
  sunburnTempModerateF: number | null;
  sunburnTempHighF: number | null;
  diseaseHumidityPct: number | null;
  diseasePrecipIn: number | null;
}

/**
 * Fetch 4-day weather forecast from core-api
 * Uses Visual Crossing integration
 * @param lat Latitude
 * @param lng Longitude
 * @param unitSystem Optional unit system preference (IMPERIAL or METRIC)
 * @param cropThresholds Optional crop-specific thresholds for metric calculations
 */
export const fetchWeatherForecast = async (
  lat: number,
  lng: number,
  unitSystem?: "IMPERIAL" | "METRIC",
  cropThresholds?: CropThresholds
): Promise<WeatherForecastResponse | null> => {
  try {
    const { data } = await coreAPI.get(`/weather/forecast`, {
      params: {
        lat,
        lng,
        unitSystem,
        ...(cropThresholds && {
          gddBaseTempF: cropThresholds.gddBaseTempF,
          chillTempLowF: cropThresholds.chillTempLowF,
          chillTempHighF: cropThresholds.chillTempHighF,
          sunburnTempModerateF: cropThresholds.sunburnTempModerateF,
          sunburnTempHighF: cropThresholds.sunburnTempHighF,
          diseaseHumidityPct: cropThresholds.diseaseHumidityPct,
          diseasePrecipIn: cropThresholds.diseasePrecipIn,
        }),
      },
    });
    return data;
  } catch (error) {
    console.error("Failed to fetch weather forecast:", error);
    return null;
  }
};

// ==================================
// REPORTS
// ==================================

export const fetchEnterpriseReports = async (
  enterpriseId: string,
  projectType?: ReportProjectType
): Promise<EnterpriseReportsResponse> => {
  const params = new URLSearchParams();
  if (projectType) {
    params.append("projectType", projectType);
  }

  const queryString = params.toString();
  const url = `/reports/enterprise/${enterpriseId}${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await coreAPI.get(url);
  return data;
};

// ==================================
// FUEL DATA
// ==================================

export interface FuelDataParams {
  year?: string; // Deprecated - use years array for multi-year support
  years?: number[]; // Multi-year support
  farmIds?: number[]; // Filter by specific farm IDs
  aggregateOnly?: boolean;
  groupBy?: "fuelType" | "field";
  fuelType?: "DIESEL" | "PROPANE" | "RED_DIESEL" | "UNLEADED_GASOLINE";
  fieldId?: string;
}

export interface FuelDataResponse {
  summary: {
    totalConsumption: number;
    totalCost: number;
    averagePrice: number;
    recordCount: number;
  };
  years: string[];
  stages: string[];
  fuelTypes: string[];
  farms: Array<{
    id: number;
    name: string;
  }>;
  volumeUnit: string; // 'gal' for Imperial, 'L' for Metric
  data: Array<{
    id: number;
    date: string;
    stage: string;
    fuelType: string;
    farmName: string;
    amount: number;
    cost: number;
    pricePerUnit: number;
    unit: string;
  }>;
}

export const getFuelData = async (
  enterpriseId: string,
  params?: FuelDataParams
): Promise<FuelDataResponse> => {
  const searchParams = new URLSearchParams();

  // Multi-year support (preferred)
  if (params?.years && params.years.length > 0) {
    searchParams.append("years", params.years.join(","));
  } else if (params?.year) {
    // Backward compatibility with single year
    searchParams.append("year", params.year);
  }

  // Farm filtering
  if (params?.farmIds && params.farmIds.length > 0) {
    searchParams.append("farmIds", params.farmIds.join(","));
  }

  if (params?.aggregateOnly) {
    searchParams.append("aggregateOnly", "true");
  }

  if (params?.groupBy) {
    searchParams.append("groupBy", params.groupBy);
  }

  if (params?.fuelType) {
    searchParams.append("fuelType", params.fuelType);
  }

  if (params?.fieldId) {
    searchParams.append("fieldId", params.fieldId);
  }

  const queryString = searchParams.toString();
  const url = `/enterprise/${enterpriseId}/fuel${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await coreAPI.get(url);
  return data;
};

// ==================================
// STATS SUMMARY (Optimized for Stats Cards)
// ==================================

/**
 * Parameters for fetching aggregated stats summary across all entity types
 * @param years - Array of years to include in calculations (e.g., [2023, 2024])
 * @param calculateAverage - If true, divides totals by number of years for true averages
 * @param farmIds - Optional filter to specific farms
 * @param cropIds - Optional filter to specific crops
 */
export interface StatsSummaryParams {
  years?: number[];
  calculateAverage?: boolean;
  farmIds?: number[];
  cropIds?: number[];
}

/**
 * Aggregated summary response for stats cards
 * Each entity type returns undefined if no data is available
 * Numeric values are either totals or averages depending on calculateAverage param
 */
export interface StatsSummaryResponse {
  cropInputs?: {
    uniqueProducts: number;
    recordCount: number;
  };
  yield?: {
    totalYield: number;
    unit: string;
    recordCount: number;
  };
  fuel?: {
    totalConsumption: number;
    totalCost: number;
    averagePrice: number;
    recordCount: number;
  };
  soil?: {
    avgOrganicMatter: number;
    avgPH?: number;
    totalFields: number;
    recordCount: number;
  };
  water?: {
    totalUsage: number;
    recordCount: number;
  };
}

/**
 * Get stats summary for all entity types (optimized for stats cards)
 * This endpoint returns aggregated summaries for all stats cards in a single request
 */
export const getStatsSummary = async (
  enterpriseId: string,
  params?: StatsSummaryParams
): Promise<StatsSummaryResponse> => {
  const searchParams = new URLSearchParams();

  if (params?.years && params.years.length > 0) {
    searchParams.append("years", params.years.join(","));
  }

  if (params?.calculateAverage !== undefined) {
    searchParams.append("calculateAverage", String(params.calculateAverage));
  }

  if (params?.farmIds && params.farmIds.length > 0) {
    searchParams.append("farmIds", params.farmIds.join(","));
  }

  if (params?.cropIds && params.cropIds.length > 0) {
    searchParams.append("cropIds", params.cropIds.join(","));
  }

  const queryString = searchParams.toString();
  const url = `/enterprise/${enterpriseId}/stats-summary${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await coreAPI.get(url);
  return data;
};

// ==================================
// SOIL DATA
// ==================================

export interface SoilDataParams {
  years?: number[];
  farmIds?: number[];
  fieldId?: string;
  metricName?: string;
}

/**
 * Response from /enterprise/:id/soil/data endpoint
 */
export interface SoilDataResponse {
  success: boolean;
  message: string;
  data: SoilDataApiResponse;
}

export const getSoilData = async (
  enterpriseId: string,
  params?: SoilDataParams
): Promise<SoilDataResponse> => {
  const searchParams = new URLSearchParams();

  if (params?.years && params.years.length > 0) {
    params.years.forEach((year) => searchParams.append("years", String(year)));
  }

  if (params?.farmIds && params.farmIds.length > 0) {
    params.farmIds.forEach((id) => searchParams.append("farmIds", String(id)));
  }

  if (params?.fieldId) {
    searchParams.append("fieldId", params.fieldId);
  }

  if (params?.metricName) {
    searchParams.append("metricName", params.metricName);
  }

  const queryString = searchParams.toString();
  const url = `/enterprise/${enterpriseId}/soil/data${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await coreAPI.get(url);
  return {
    success: true,
    message: "Success",
    data,
  };
};

/**
 * Soil metric option from the API
 */
export interface SoilMetricOption {
  id: number;
  name: string;
  description?: string | null;
}

/**
 * Fetch all soil metrics for an enterprise
 */
export const fetchSoilMetrics = async (
  enterpriseId: string
): Promise<SoilMetricOption[]> => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/soil/metrics`
  );
  return data;
};

// ==================================
// AUDITS & REPORTING
// ==================================

export const fetchAuditTemplates = async () => {
  const { data } = await coreAPI.get(`/audit-templates`);
  return data;
};

export const fetchCustomAuditTemplates = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/custom-audit-templates`
  );
  return data;
};

export const deleteCustomAuditTemplate = async (
  enterpriseId: string,
  templateId: number
) => {
  const { data } = await coreAPI.delete(
    `/enterprise/${enterpriseId}/custom-audit-templates/${templateId}`
  );
  return data;
};

export const cloneDefaultToCustomTemplate = async (
  enterpriseId: string,
  templateId: number
) => {
  const { data } = await coreAPI.post(
    `/enterprise/${enterpriseId}/custom-audit-templates/clone-from-default`,
    { templateId }
  );
  return data;
};

export type CreatePendingCustomAuditTemplatePayload = {
  rawCustomerDataId: number;
  title?: string;
  description?: string;
};

/**
 * Create a PENDING custom audit template as a placeholder while ETL processes.
 * The ETL pipeline will later update this record with the actual questions.
 */
export const createPendingCustomAuditTemplate = async (
  enterpriseId: string,
  payload: CreatePendingCustomAuditTemplatePayload
) => {
  const { data } = await coreAPI.post(
    `/enterprise/${enterpriseId}/custom-audit-templates`,
    payload
  );
  return data;
};

export const fetchCustomAuditTemplateById = async (
  enterpriseId: string,
  templateId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/custom-audit-templates/${templateId}`
  );
  return data;
};

export type CustomAuditTemplateQuestion = {
  section: string;
  code: string;
  question: string;
  criteria: string;
  level: string;
  answer?: string | null;
  reasoning?: string | null;
};

export type UpdateCustomAuditTemplatePayload = {
  title?: string;
  description?: string | null;
  questions?: CustomAuditTemplateQuestion[];
};

export const updateCustomAuditTemplate = async (
  enterpriseId: string,
  templateId: string,
  payload: UpdateCustomAuditTemplatePayload
) => {
  const { data } = await coreAPI.patch(
    `/enterprise/${enterpriseId}/custom-audit-templates/${templateId}`,
    payload
  );
  return data;
};

export const fetchEnterpriseAudits = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(`/enterprise/${enterpriseId}/audits`);
  return data;
};

export const fetchEnterpriseAuditById = async (
  enterpriseId: string,
  auditId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/audits/${auditId}`
  );
  return data;
};

export const fetchEnterpriseAudit = async (
  enterpriseId: string,
  auditId: number | string
) => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/audits/${auditId}`
  );
  return data;
};

export type AuditStatus = "draft" | "in_progress" | "completed" | "archived";

export type CreateAuditPayload = {
  templateId?: number;
  customTemplateId?: number;
  responses?: Record<string, unknown>;
  status?: AuditStatus;
};

export const createEnterpriseAudit = async (
  enterpriseId: string,
  payload: CreateAuditPayload
) => {
  const { data } = await coreAPI.post(
    `/enterprise/${enterpriseId}/audits`,
    payload
  );
  return data;
};

export type AuditReportExportPayload = {
  formats: string[];
  includeReasonings?: boolean;
  includeSourceFiles?: boolean;
};

export type UpdateAuditPayload = {
  responses?: Record<string, unknown>;
  status?: string;
  answer?: string | null;
  title?: string;
  sourceRawCustomerDataIds?: number[];
};

export const downloadEnterpriseAuditReport = async (
  enterpriseId: string,
  auditId: string,
  payload: AuditReportExportPayload
): Promise<AxiosResponse<Blob>> => {
  return coreAPI.post(
    `/enterprise/${enterpriseId}/audits/${auditId}/export`,
    payload,
    { responseType: "blob" }
  );
};

export const updateEnterpriseAudit = async (
  enterpriseId: string,
  auditId: string,
  payload: UpdateAuditPayload
) => {
  const { data } = await coreAPI.patch(
    `/enterprise/${enterpriseId}/audits/${auditId}`,
    payload
  );

  return data;
};

export const deleteEnterpriseAudit = async (
  enterpriseId: string,
  auditId: string
) => {
  const { data } = await coreAPI.delete(
    `/enterprise/${enterpriseId}/audits/${auditId}`
  );

  return data;
};

export const updateAuditQuestion = async (
  enterpriseId: string,
  auditId: number | string,
  questionId: number,
  payload: UpdateAuditPayload
) => {
  const { data } = await coreAPI.patch(
    `/enterprise/${enterpriseId}/audits/${auditId}/questions/${questionId}`,
    payload
  );

  return data;
};

export const fetchAuditCertificates = async (
  enterpriseId: string,
  auditId: string
) => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/audits/${auditId}/certificates`
  );
  return data;
};

// ==================================
// BENCHMARKING
// ==================================

export const fetchBenchmarkData = async (
  enterpriseId: string,
  filters?: {
    tenantType?: "crop" | "variety" | "field" | "farm" | "enterprise";
    metricCategory?: "yield" | "productivity" | "efficiency" | "crop_inputs";
    benchmarkType?: "internal" | "peer";
    period?: string;
  }
) => {
  const { data } = await coreAPI.get(`/benchmarks/enterprise/${enterpriseId}`, {
    params: filters,
  });
  return data;
};

export const fetchBenchmarkingCommunity = async (
  enterpriseId: string,
  filters?: {
    cropId?: number;
    benchmarkType?: "peer" | "external";
    unitSystem?: "IMPERIAL" | "METRIC";
  }
) => {
  const params = new URLSearchParams();
  if (filters?.cropId) params.append("cropId", filters.cropId.toString());
  if (filters?.benchmarkType)
    params.append("benchmarkType", filters.benchmarkType);
  if (filters?.unitSystem) params.append("unitSystem", filters.unitSystem);

  const { data } = await coreAPI.get(
    `/benchmarks/enterprise/${enterpriseId}/community?${params.toString()}`
  );
  return data;
};

export const deleteAuditCertificate = async (
  enterpriseId: string,
  auditId: string,
  certificateId: number
) => {
  const { data } = await coreAPI.delete(
    `/enterprise/${enterpriseId}/audits/${auditId}/certificates/${certificateId}`
  );
  return data;
};

export const linkAuditCertificates = async (
  enterpriseId: string,
  auditId: string,
  rawCustomerDataIds: number[]
) => {
  const { data } = await coreAPI.post(
    `/enterprise/${enterpriseId}/audits/${auditId}/certificates/link`,
    { rawCustomerDataIds }
  );
  return data;
};

export const resetEnterpriseAudits = async (enterpriseId: string) => {
  const { data } = await coreAPI.delete(
    `/enterprise/${enterpriseId}/audits/reset`
  );
  return data;
};

// ==================================
// CROP INPUT CATEGORY
// ==================================

export type CropInputCategory =
  | "fertilizer"
  | "insecticide"
  | "fungicide"
  | "herbicide"
  | "totalPesticide";

// ==================================
// WATER DATA
// ==================================

export interface WaterDataParams {
  year?: string; // Deprecated - use years array for multi-year support
  years?: number[]; // Multi-year support
  farmIds?: number[]; // Filter by specific farm IDs
  fieldId?: string;
  systemType?: string;
  limit?: number;
  offset?: number;
  groupBy?: "systemType" | "field" | "year" | "month";
  aggregateOnly?: boolean;
}

export interface WaterDataResponse {
  success?: boolean;
  message?: string;
  data: any[]; // Will be defined more specifically as we implement individual data points
  summary?: {
    totalUsage: number;
    recordCount: number;
  };
  aggregated?: Record<
    string,
    {
      usage: number;
      recordCount: number;
    }
  >;
  years?: string[];
  waterSources?: string[];
  farms?: Array<{ id: number; name: string }>;
  areaUnit?: string;
  volumeUnit?: string;
  usageUnit?: string;
  monthlyUsage?: Array<{ label: string; value: number }>;
  monthlyUsageVolume?: Array<{ label: string; value: number }>;
}

export const getWaterYears = async (
  enterpriseId: string
): Promise<string[]> => {
  const url = `/enterprise/${enterpriseId}/water/years`;
  const { data } = await coreAPI.get(url);
  return data;
};

export const getWaterData = async (
  enterpriseId: string,
  params?: WaterDataParams
): Promise<WaterDataResponse> => {
  const searchParams = new URLSearchParams();

  // Multi-year support (preferred)
  if (params?.years && params.years.length > 0) {
    searchParams.append("years", params.years.join(","));
  } else if (params?.year) {
    // Backward compatibility with single year
    searchParams.append("year", params.year);
  }

  // Farm filtering
  if (params?.farmIds && params.farmIds.length > 0) {
    searchParams.append("farmIds", params.farmIds.join(","));
  }

  if (params?.fieldId) {
    searchParams.append("fieldId", params.fieldId);
  }

  if (params?.systemType) {
    searchParams.append("systemType", params.systemType);
  }

  if (params?.limit !== undefined) {
    searchParams.append("limit", params.limit.toString());
  }

  if (params?.offset !== undefined) {
    searchParams.append("offset", params.offset.toString());
  }

  const queryString = searchParams.toString();
  const url = `/enterprise/${enterpriseId}/water${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await coreAPI.get(url);
  return {
    success: true,
    message: "Success",
    data: data.data || [], // Extract the data array from WaterDataResponseEntity
    summary: data.summary,
    areaUnit: data.areaUnit,
    volumeUnit: data.volumeUnit,
    usageUnit: data.usageUnit,
    monthlyUsage: data.monthlyUsage,
    monthlyUsageVolume: data.monthlyUsageVolume,
  };
};

// ==================================
// FINANCIALS
// ==================================

// Raw data structure from JSON
export interface FinancialRecord {
  year: number;
  crop: string;
  field: string;
  variety: string;
  type: "Cost" | "Revenue";
  category: string;
  costType?: "direct" | "indirect"; // Classification for cost records
  amount: number;
  area: number; // Area in user's preferred unit (acres or hectares)
  areaUnit: string; // Area unit label (ac or ha)
}

export interface FinancialProfitMetric {
  crop: string;
  title: string;
  value: string;
  comparison?: string;
  comparisonValue?: number;
  trend?: "up" | "down" | "flat";
  sentiment?: "positive" | "negative" | "neutral";
}

export interface FinancialsMetadata {
  requestedYear?: number;
  returnedYear: number | null;
  availableYears: number[];
}

export interface RawFinancialsData {
  profitMetrics: FinancialProfitMetric[];
  records: FinancialRecord[];
  metadata?: FinancialsMetadata;
  areaUnit: string; // Area unit label for the response (ac or ha)
}

export const fetchFinancialsData = async (
  enterpriseId: string,
  year?: string
): Promise<RawFinancialsData> => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/financials/summary`,
    {
      params: year ? { year } : {},
    }
  );

  return data as RawFinancialsData;
};

// ==================================
// INTEGRATIONS
// ==================================

export interface Integration {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  description?: string;
  isEnabled: boolean;
  credentialsPersist: boolean;
  recurringSync: boolean;
  credentials?: string;
  enterpriseId: number;
  createdAt: string;
  updatedAt?: string;
  isAvailable?: boolean;
  importScheduleCron?: string | null;
  importScheduleActive?: boolean;
  lastScheduledRunAt?: string | null;
  requires2FA?: boolean;
}

export interface CreateIntegrationPayload {
  name: string;
  slug: string;
  icon?: string;
  description?: string;
  url?: string;
  isEnabled?: boolean;
  credentialsPersist?: boolean;
  recurringSync?: boolean;
  fmsUsername?: string;
  fmsPassword?: string;
  requires2FA?: boolean;
  otpConfig?: {
    senderFilter?: string;
    subjectFilter?: string;
    otpPattern?: string;
  } | null;
}

export interface UpdateIntegrationPayload {
  name?: string;
  icon?: string;
  description?: string;
  url?: string;
  isEnabled?: boolean;
  credentialsPersist?: boolean;
  recurringSync?: boolean;
  fmsUsername?: string;
  fmsPassword?: string;
  importScheduleCron?: string | null;
  importScheduleActive?: boolean;
  requires2FA?: boolean;
  otpConfig?: {
    senderFilter?: string;
    subjectFilter?: string;
    otpPattern?: string;
  } | null;
}

/**
 * Get all integrations for an enterprise
 */
export const getEnterpriseIntegrations = async (
  enterpriseId: string
): Promise<Integration[]> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/integrations`
  );
  return data;
};

/**
 * Create a new integration for an enterprise
 * If credentialsPersist is true and credentials are provided,
 * they will be stored in Google Secret Manager
 */
export const createEnterpriseIntegration = async (
  enterpriseId: string,
  payload: CreateIntegrationPayload
): Promise<Integration> => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/integrations`,
    payload
  );
  return data;
};

/**
 * Update an existing integration
 * If credentialsPersist is true and new credentials are provided,
 * they will be updated in Google Secret Manager
 */
export const updateEnterpriseIntegration = async (
  enterpriseId: string,
  integrationId: string,
  payload: UpdateIntegrationPayload
): Promise<Integration> => {
  const { data } = await coreAPI.patch(
    `/enterprises/${enterpriseId}/integrations/${integrationId}`,
    payload
  );
  return data;
};

export const deleteEnterpriseIntegration = async (
  enterpriseId: string,
  integrationId: string
): Promise<void> => {
  await coreAPI.delete(
    `/enterprises/${enterpriseId}/integrations/${integrationId}`
  );
};

export interface TriggerAgentPayload {
  integrationName: string;
  integrationUrl?: string;
  username?: string;
  password?: string;
}

export interface TriggerAgentResponse {
  success?: boolean;
  job_id: number;
  jobId?: number;
  id?: number;
  message: string;
}

/**
 * Trigger BX Agent for an integration
 * Starts a scraping job with the provided credentials and URL
 */
export const triggerAgent = async (
  enterpriseId: string,
  integrationId: string,
  payload: TriggerAgentPayload
): Promise<TriggerAgentResponse> => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/integrations/${integrationId}/trigger-agent`,
    payload
  );
  return data;
};

// ==================================
// CLOUD DRIVE ENDPOINTS
// ==================================

export interface CloudDriveItem {
  id: string;
  name: string;
  type: "folder" | "file";
  mimeType?: string;
  size?: number;
  modifiedTime?: string;
  childCount?: number;
  driveId?: string;
  path?: string;
}

export interface ServiceAccountInfo {
  email: string;
  instructions: string;
}

export interface VerifyAccessResponse {
  success: boolean;
  sharedItemCount: number;
  itemName?: string;
  itemType?: "file" | "folder";
  itemId?: string;
  /** @deprecated use itemId */
  folderId?: string;
  driveId?: string;
  /** @deprecated use itemName */
  folderName?: string;
  message?: string;
}

export interface FolderSelection {
  id: string;
  label: string;
  driveId?: string;
  type?: "file" | "folder";
}

export const getCloudDriveServiceAccountInfo = async (
  enterpriseId: string,
  integrationSlug: string
): Promise<ServiceAccountInfo> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/integrations/${integrationSlug}/cloud-drive/service-account-info`
  );
  return data;
};

export const verifyCloudDriveAccess = async (
  enterpriseId: string,
  integrationSlug: string,
  folderUrl: string
): Promise<VerifyAccessResponse> => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/integrations/${integrationSlug}/cloud-drive/verify-access`,
    { folderUrl }
  );
  return data;
};

export const listCloudDriveFiles = async (
  enterpriseId: string,
  integrationSlug: string,
  folderId?: string,
  driveId?: string
): Promise<CloudDriveItem[]> => {
  const params = new URLSearchParams();
  if (folderId !== undefined && folderId !== null)
    params.set("folderId", folderId);
  if (driveId) params.set("driveId", driveId);
  const query = params.toString() ? `?${params.toString()}` : "";
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/integrations/${integrationSlug}/cloud-drive/files${query}`
  );
  return data;
};

export const getCloudDriveSavedFolders = async (
  enterpriseId: string,
  integrationSlug: string
): Promise<{ folders: FolderSelection[] }> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/integrations/${integrationSlug}/cloud-drive/saved-folders`
  );
  return data;
};

export const saveCloudDriveFolderSelection = async (
  enterpriseId: string,
  integrationSlug: string,
  folders: FolderSelection[],
  replace?: boolean
): Promise<Integration> => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/integrations/${integrationSlug}/cloud-drive/save-folder-selection`,
    { folders, ...(replace && { replace }) }
  );
  return data;
};

// ==================================
// DROPBOX OAUTH
// ==================================

export interface DropboxAuthInfo {
  authType: "oauth";
  instructions: string;
  isConnected: boolean;
}

export interface OneDriveAuthInfo {
  authType: "oauth" | "service-account";
  instructions?: string;
  isConnected: boolean;
}

export const getDropboxAuthInfo = async (
  enterpriseId: string
): Promise<DropboxAuthInfo> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/integrations/dropbox/cloud-drive/auth-info`
  );
  return data;
};

export const getDropboxAuthorizeUrl = async (
  enterpriseId: string,
  redirectUri: string
): Promise<{ authorizationUrl: string }> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/integrations/dropbox/cloud-drive/oauth/authorize-url`,
    { params: { redirectUri } }
  );
  return data;
};

export const exchangeDropboxCode = async (
  enterpriseId: string,
  code: string,
  redirectUri: string,
  state?: string
): Promise<{ success: boolean; accountId: string }> => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/integrations/dropbox/cloud-drive/oauth/callback`,
    { code, redirectUri, state }
  );
  return data;
};

export const getOneDriveAuthInfo = async (
  enterpriseId: string
): Promise<OneDriveAuthInfo> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/integrations/onedrive/cloud-drive/auth-info`
  );
  return data;
};

export const getOneDriveAuthorizeUrl = async (
  enterpriseId: string,
  redirectUri: string
): Promise<{ authorizationUrl: string }> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/integrations/onedrive/cloud-drive/oauth/authorize-url`,
    { params: { redirectUri } }
  );
  return data;
};

export const exchangeOneDriveCode = async (
  enterpriseId: string,
  code: string,
  redirectUri: string,
  state?: string
): Promise<{ success: boolean; accountId: string }> => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/integrations/onedrive/cloud-drive/oauth/callback`,
    { code, redirectUri, state }
  );
  return data;
};

// ==================================
// DATA IMPORT JOBS
// ==================================

export interface DataImportJob {
  id: number;
  enterpriseId: number;
  integrationId: string;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  trigger: "manual" | "scheduled";
  currentPhase?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  durationSeconds?: number | null;
  recordsFound?: number | null;
  dataExtractedMB?: number | null;
  filesCount?: number | null;
  errorMessage?: string | null;
  instructions?: string | null;
  debugUrl?: string | null;
  rawCustomerData?: Array<{
    id: number;
    fileName: string | null;
    fileType: string | null;
    tags: string[];
    createdAt: string;
  }> | null;
  createdAt: string;
  updatedAt: string;
  integration?: {
    id: string;
    name: string;
    slug: string;
  };
}

export interface DataImportEvent {
  id: number;
  jobId: number;
  phase: string;
  message: string;
  timestamp: string;
  metadata?: Record<string, unknown> | null;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/** Normalize API job response to match DataImportJob interface */
function normalizeJob(raw: any): DataImportJob {
  const rawStatus = (raw.status || "pending").toLowerCase();
  const statusMap: Record<string, DataImportJob["status"]> = {
    succeeded: "completed",
    partially_succeeded: "completed",
    completed: "completed",
    failed: "failed",
    cancelled: "cancelled",
    canceled: "cancelled",
    running: "running",
    pending: "pending",
  };

  return {
    ...raw,
    status: statusMap[rawStatus] || rawStatus,
    trigger: (raw.triggerType || raw.trigger || "manual").toLowerCase(),
    recordsFound: raw.recordsFound ?? raw.stats?.recordsFound ?? null,
    filesCount: raw.filesCount ?? raw.stats?.imported ?? null,
    currentPhase: raw.currentPhase ?? raw.changes?.phase ?? null,
    debugUrl: raw.debugUrl ?? raw.debug_url ?? null,
    rawCustomerData: raw.rawCustomerData ?? null,
  };
}

/** Normalize API event response to match DataImportEvent interface */
function normalizeEvent(raw: any): DataImportEvent {
  return {
    id: raw.id,
    jobId: raw.entityId ?? raw.jobId,
    phase: raw.changes?.phase ?? raw.phase ?? "",
    message: raw.changes?.message ?? raw.message ?? "",
    timestamp: raw.changeTimestamp ?? raw.timestamp ?? "",
    metadata: raw.changes?.metadata ?? raw.metadata ?? null,
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * List data import jobs for an enterprise
 */
export const getDataImportJobs = async (
  enterpriseId: string,
  params?: {
    status?: string;
    integrationId?: string;
    limit?: number;
    offset?: number;
  }
): Promise<DataImportJob[]> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/data-import-jobs`,
    { params }
  );
  return (data || []).map(normalizeJob);
};

/**
 * Get a single data import job with details
 */
export const getDataImportJob = async (
  enterpriseId: string,
  jobId: string
): Promise<DataImportJob> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/data-import-jobs/${jobId}`
  );
  return normalizeJob(data);
};

/**
 * Get timeline events for a data import job
 */
export const getDataImportJobEvents = async (
  enterpriseId: string,
  jobId: string
): Promise<DataImportEvent[]> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/data-import-jobs/${jobId}/events`
  );
  const events: DataImportEvent[] = (data || []).map(normalizeEvent);

  // Carry forward last known phase to events that don't set one explicitly
  let lastPhase = "";
  events.forEach((event) => {
    if (event.phase) {
      lastPhase = event.phase;
    } else {
      // eslint-disable-next-line no-param-reassign
      event.phase = lastPhase;
    }
  });

  return events;
};

/**
 * Cancel a running data import job
 */
export const cancelDataImportJob = async (
  enterpriseId: string,
  jobId: string
): Promise<void> => {
  await coreAPI.post(
    `/enterprises/${enterpriseId}/data-import-jobs/${jobId}/cancel`
  );
};

// ==================================
// PINNED ITEMS
// ==================================

/**
 * Get all pinned items for an enterprise
 * @param enterpriseId - The enterprise ID
 * @param itemType - Optional filter by item type (SAM_MESSAGE or COMPONENT)
 */
export const fetchPinnedItems = async (
  enterpriseId: string,
  itemType?: "SAM_MESSAGE" | "COMPONENT"
) => {
  const params = new URLSearchParams();
  if (itemType) {
    params.append("itemType", itemType);
  }

  const queryString = params.toString();
  const url = `/enterprises/${enterpriseId}/pinned-items${
    queryString ? `?${queryString}` : ""
  }`;

  const { data } = await coreAPI.get(url);
  return data;
};

/**
 * Create a new pinned item
 * @param enterpriseId - The enterprise ID
 * @param payload - The pinned item data
 */
export const createPinnedItem = async (
  enterpriseId: string,
  payload: {
    itemType: "SAM_MESSAGE" | "COMPONENT";
    name: string;
    description?: string;
    messageText?: string;
    messageId?: string;
    conversationId?: string;
    userQuestion?: string;
    componentId?: string;
    parameters?: Record<string, any>;
    scope?: "PERSONAL" | "ENTERPRISE";
  }
) => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/pinned-items`,
    payload
  );
  return data;
};

/**
 * Delete a pinned item
 * @param enterpriseId - The enterprise ID
 * @param itemId - The pinned item ID
 */
export const deletePinnedItem = async (
  enterpriseId: string,
  itemId: string
) => {
  await coreAPI.delete(`/enterprises/${enterpriseId}/pinned-items/${itemId}`);
};

/**
 * Update a pinned item (e.g., displayOrder for reordering)
 * @param enterpriseId - The enterprise ID
 * @param itemId - The pinned item ID
 * @param payload - The update payload
 */
export const updatePinnedItem = async (
  enterpriseId: string,
  itemId: string,
  payload: {
    displayOrder?: number;
    name?: string;
    scope?: "PERSONAL" | "ENTERPRISE";
  }
): Promise<PinnedItem> => {
  const { data } = await coreAPI.patch(
    `/enterprises/${enterpriseId}/pinned-items/${itemId}`,
    payload
  );
  return data;
};

// ==================================
// ANALYTICS API
// ==================================

/**
 * Get available data sources for analytics dashboards
 * @param enterpriseId - The enterprise ID
 */
export const getAnalyticsDataSources = async (
  enterpriseId: string
): Promise<import("@/types/api").DataSourcesResponse> => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/analytics/data-sources`
  );
  return data;
};

/**
 * Get available dimensions and measures for selected data sources
 * @param enterpriseId - The enterprise ID
 * @param payload - The selected data sources
 */
export const getAnalyticsFields = async (
  enterpriseId: string,
  payload: import("@/types/api").FieldsRequest
): Promise<import("@/types/api").FieldsResponse> => {
  const { data } = await coreAPI.post(
    `/enterprise/${enterpriseId}/analytics/fields`,
    payload
  );
  return data;
};

/**
 * Execute analytics query and get preview data
 * @param enterpriseId - The enterprise ID
 * @param payload - The analytics query configuration
 */
export const getAnalyticsPreview = async (
  enterpriseId: string,
  payload: import("@/types/api").PreviewRequest
): Promise<import("@/types/api").PreviewResponse> => {
  const { data } = await coreAPI.post(
    `/enterprise/${enterpriseId}/analytics/preview`,
    payload
  );
  return data;
};

// ==================================
// CUSTOM DASHBOARDS API
// ==================================

/**
 * Fetch all custom dashboards for an enterprise
 * @param enterpriseId - The enterprise ID
 */
export const fetchCustomDashboards = async (
  enterpriseId: string
): Promise<import("@/types/customDashboard").CustomDashboardListItem[]> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/custom-dashboards`
  );
  return data;
};

/**
 * Fetch a single custom dashboard with its widgets
 * @param enterpriseId - The enterprise ID
 * @param dashboardId - The dashboard UUID
 */
export const fetchCustomDashboard = async (
  enterpriseId: string,
  dashboardId: string
): Promise<import("@/types/customDashboard").CustomDashboardDetail> => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/custom-dashboards/${dashboardId}`
  );
  return data;
};

/**
 * Log a dashboard-intent event for the BX Tech "Build with SAM" analytics
 * trail. Backed by core-api's AuditLog (entityType="DashboardIntent") via
 * the endpoint added in PR #1078.
 *
 * Silent fire-and-forget: callers should treat failures as no-ops. The
 * surface is purely additive — losing one event doesn't break any UX —
 * and the endpoint is not deployed in every environment yet (depends on
 * PR #1078 landing). We log to console in dev so issues surface during
 * development without bothering users.
 */
export const postDashboardIntentEvent = async (
  enterpriseId: string,
  event: import("@/types/dashboardIntent").DashboardIntentEvent
): Promise<void> => {
  try {
    await coreAPI.post(
      `/enterprises/${enterpriseId}/dashboard-intent-events`,
      event
    );
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[dashboardIntent] log failed (non-fatal)", err);
    }
  }
};

/**
 * Create a new custom dashboard
 * @param enterpriseId - The enterprise ID
 * @param payload - The dashboard creation payload
 */
export const createCustomDashboard = async (
  enterpriseId: string,
  payload: import("@/types/customDashboard").CreateCustomDashboardPayload
): Promise<import("@/types/customDashboard").CustomDashboardDetail> => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/custom-dashboards`,
    payload
  );
  return data;
};

/**
 * Update a custom dashboard
 * @param enterpriseId - The enterprise ID
 * @param dashboardId - The dashboard UUID
 * @param payload - The update payload
 */
export const updateCustomDashboard = async (
  enterpriseId: string,
  dashboardId: string,
  payload: import("@/types/customDashboard").UpdateCustomDashboardPayload
): Promise<import("@/types/customDashboard").CustomDashboardDetail> => {
  const { data } = await coreAPI.patch(
    `/enterprises/${enterpriseId}/custom-dashboards/${dashboardId}`,
    payload
  );
  return data;
};

/**
 * Delete a custom dashboard
 * @param enterpriseId - The enterprise ID
 * @param dashboardId - The dashboard UUID
 */
export const deleteCustomDashboard = async (
  enterpriseId: string,
  dashboardId: string
): Promise<void> => {
  await coreAPI.delete(
    `/enterprises/${enterpriseId}/custom-dashboards/${dashboardId}`
  );
};

/**
 * Add a widget to a custom dashboard
 * @param enterpriseId - The enterprise ID
 * @param dashboardId - The dashboard UUID
 * @param payload - The widget creation payload
 */
export const createDashboardWidget = async (
  enterpriseId: string,
  dashboardId: string,
  payload: import("@/types/customDashboard").CreateWidgetPayload
): Promise<import("@/types/customDashboard").DashboardWidgetDto> => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/custom-dashboards/${dashboardId}/widgets`,
    payload
  );
  return data;
};

/**
 * Update a widget in a custom dashboard
 * @param enterpriseId - The enterprise ID
 * @param dashboardId - The dashboard UUID
 * @param widgetId - The widget UUID
 * @param payload - The update payload
 */
export const updateDashboardWidget = async (
  enterpriseId: string,
  dashboardId: string,
  widgetId: string,
  payload: import("@/types/customDashboard").UpdateWidgetPayload
): Promise<import("@/types/customDashboard").DashboardWidgetDto> => {
  const { data } = await coreAPI.patch(
    `/enterprises/${enterpriseId}/custom-dashboards/${dashboardId}/widgets/${widgetId}`,
    payload
  );
  return data;
};

/**
 * Delete a widget from a custom dashboard
 * @param enterpriseId - The enterprise ID
 * @param dashboardId - The dashboard UUID
 * @param widgetId - The widget UUID
 */
export const deleteDashboardWidget = async (
  enterpriseId: string,
  dashboardId: string,
  widgetId: string
): Promise<void> => {
  await coreAPI.delete(
    `/enterprises/${enterpriseId}/custom-dashboards/${dashboardId}/widgets/${widgetId}`
  );
};

// ==================================
// ENTERPRISE PREFERENCES
// ==================================

export const getEnterprisePreferences = async (
  enterpriseId: string
): Promise<EnterprisePreferences> => {
  const response = await coreAPI.get(
    `/enterprises/${enterpriseId}/preferences`
  );
  return response.data;
};

export const updateEnterprisePreferences = async (
  enterpriseId: string,
  data: Partial<EnterprisePreferences>
): Promise<EnterprisePreferences> => {
  const response = await coreAPI.patch(
    `/enterprises/${enterpriseId}/preferences`,
    data
  );
  return response.data;
};

export const uploadEnterpriseLogo = async (
  enterpriseId: string,
  file: File
) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await coreAPI.patch(
    `/enterprises/${enterpriseId}/logo`,
    formData
  );
  return data;
};

export const deleteEnterpriseLogo = async (enterpriseId: string) => {
  const { data } = await coreAPI.delete(`/enterprises/${enterpriseId}/logo`);
  return data;
};

// ==================================
// CUSTOM UNITS
// ==================================

export const getCustomUnits = async (
  enterpriseId: string
): Promise<CustomUnit[]> => {
  const response = await coreAPI.get(
    `/enterprises/${enterpriseId}/custom-units`
  );
  return response.data;
};

export const createCustomUnit = async (
  enterpriseId: string,
  data: Omit<
    CustomUnit,
    | "id"
    | "createdAt"
    | "updatedAt"
    | "enterpriseId"
    | "createdByUserId"
    | "crop"
    | "variety"
  >
): Promise<CustomUnit> => {
  const response = await coreAPI.post(
    `/enterprises/${enterpriseId}/custom-units`,
    data
  );
  return response.data;
};

export const updateCustomUnit = async (
  enterpriseId: string,
  unitId: number,
  data: Partial<CustomUnit>
): Promise<CustomUnit> => {
  const response = await coreAPI.patch(
    `/enterprises/${enterpriseId}/custom-units/${unitId}`,
    data
  );
  return response.data;
};

export const deleteCustomUnit = async (
  enterpriseId: string,
  unitId: number
): Promise<void> => {
  await coreAPI.delete(`/enterprises/${enterpriseId}/custom-units/${unitId}`);
};

export const fetchNonStandardUnits = async (
  enterpriseId: string
): Promise<{ id: number; name: string; displayName?: string }[]> => {
  const response = await coreAPI.get(
    `/enterprises/${enterpriseId}/custom-units/non-standard`
  );
  return response.data;
};

// ==================================
// UNIT SYSTEM DEFAULTS
// ==================================

export const fetchUnitSystemDefaults = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/units/system-defaults`
  );
  return data;
};

// Financial Categories
export const getFinancialCategories = async (
  enterpriseId: string
): Promise<FinancialCategory[]> => {
  const response = await coreAPI.get(
    `/enterprise/${enterpriseId}/financial-categories`
  );
  return response.data;
};

export const createFinancialCategory = async (
  enterpriseId: string,
  data: { name: string; label: string; directCost: boolean; notes?: string }
): Promise<FinancialCategory> => {
  const response = await coreAPI.post(
    `/enterprise/${enterpriseId}/financial-categories`,
    data
  );
  return response.data;
};

export const updateFinancialCategory = async (
  enterpriseId: string,
  categoryId: number,
  data: Partial<{
    name: string;
    label: string;
    directCost: boolean;
    notes: string;
    isActive: boolean;
  }>,
  reassignTo?: number
): Promise<FinancialCategory> => {
  const params = reassignTo ? `?reassignTo=${reassignTo}` : "";
  const response = await coreAPI.patch(
    `/enterprise/${enterpriseId}/financial-categories/${categoryId}${params}`,
    data
  );
  return response.data;
};

export const deleteFinancialCategory = async (
  enterpriseId: string,
  categoryId: number,
  reassignTo?: number
): Promise<void> => {
  const params = reassignTo ? `?reassignTo=${reassignTo}` : "";
  await coreAPI.delete(
    `/enterprise/${enterpriseId}/financial-categories/${categoryId}${params}`
  );
};

// Harvest Cycles
export const getEnterpriseHarvestCycles = async (
  enterpriseId: string
): Promise<EnterpriseHarvestCycle[]> => {
  const response = await coreAPI.get(
    `/enterprises/${enterpriseId}/harvest-cycles`
  );
  return response.data;
};

export const createEnterpriseHarvestCycle = async (
  enterpriseId: string,
  data: { cropId: number; startMonth: number; endMonth: number }
): Promise<EnterpriseHarvestCycle> => {
  const response = await coreAPI.post(
    `/enterprises/${enterpriseId}/harvest-cycles`,
    data
  );
  return response.data;
};

export const updateEnterpriseHarvestCycle = async (
  enterpriseId: string,
  cycleId: number,
  data: Partial<{ cropId: number; startMonth: number; endMonth: number }>
): Promise<EnterpriseHarvestCycle> => {
  const response = await coreAPI.patch(
    `/enterprises/${enterpriseId}/harvest-cycles/${cycleId}`,
    data
  );
  return response.data;
};

export const deleteEnterpriseHarvestCycle = async (
  enterpriseId: string,
  cycleId: number
): Promise<void> => {
  await coreAPI.delete(
    `/enterprises/${enterpriseId}/harvest-cycles/${cycleId}`
  );
};

// ==================================
// ONBOARDING
// ==================================

export const getOnboardingProgress = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(
    `/enterprises/${enterpriseId}/onboarding/progress`
  );
  return data;
};

export const startOnboarding = async (enterpriseId: string) => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/onboarding/start`
  );
  return data;
};

export const completeOnboarding = async (enterpriseId: string) => {
  const { data } = await coreAPI.post(
    `/enterprises/${enterpriseId}/onboarding/complete`
  );
  return data;
};

// ==================================
// ENTERPRISE MEMBERS
// ==================================

export const fetchEnterpriseMembers = async (enterpriseId: string) => {
  const res = await coreAPI.get(`/enterprises/${enterpriseId}/members`);
  return res.data;
};

export const fetchEnterpriseMemberStats = async (enterpriseId: string) => {
  const res = await coreAPI.get(`/enterprises/${enterpriseId}/members/stats`);
  return res.data;
};

export const inviteEnterpriseMember = async (
  enterpriseId: string,
  data: InviteMemberPayload
) => {
  const res = await coreAPI.post(
    `/enterprises/${enterpriseId}/members/invite`,
    data
  );
  return res.data;
};

export const updateEnterpriseMember = async (
  enterpriseId: string,
  userId: number,
  data: UpdateMemberPayload
) => {
  const res = await coreAPI.patch(
    `/enterprises/${enterpriseId}/members/${userId}`,
    data
  );
  return res.data;
};

export const removeEnterpriseMember = async (
  enterpriseId: string,
  userId: number
) => {
  const res = await coreAPI.delete(
    `/enterprises/${enterpriseId}/members/${userId}`
  );
  return res.data;
};

// ─── Pack-Out API ──────────────────────────────────────────────────────────

// Sorting-line source systems vary per enterprise — the legit set comes
// from the dashboard metadata endpoint (`metadata.sourceSystems[]`), not
// from a fixed allow-list. Keep this as `string` so any code emitted by
// pack-out ingestion (COMPAC, BUHLER, MAF, UNITEC, AGROTS, …) flows
// through the filter chip and the API.
export type PackOutSourceSystem = string;

export interface PackOutDashboardParams {
  startDate?: string;
  endDate?: string;
  varietyNames?: string;
  sourceSystem?: PackOutSourceSystem;
  /** Single grower code (e.g. partnership.externalCode). Filters all dashboard endpoints via buildWhereClause. */
  growerCode?: string;
  /** Comma-separated run numbers, scoped to Quality lens drill-downs. */
  runNumbers?: string;
}

const buildPackOutSearchParams = (params?: PackOutDashboardParams) => {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.append("startDate", params.startDate);
  if (params?.endDate) searchParams.append("endDate", params.endDate);
  if (params?.varietyNames)
    searchParams.append("varietyNames", params.varietyNames);
  if (params?.sourceSystem)
    searchParams.append("sourceSystem", params.sourceSystem);
  if (params?.growerCode) searchParams.append("growerCode", params.growerCode);
  if (params?.runNumbers) searchParams.append("runNumbers", params.runNumbers);
  return searchParams;
};

export const getPackOutDashboard = async (
  enterpriseId: string,
  params?: PackOutDashboardParams
) => {
  const qs = buildPackOutSearchParams(params).toString();
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/pack-outs/dashboard${qs ? `?${qs}` : ""}`
  );
  return data;
};

export const getPackOutDashboardMetadata = async (enterpriseId: string) => {
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/pack-outs/dashboard/metadata`
  );
  return data;
};

export const getPackOutDashboardGrowers = async (
  enterpriseId: string,
  params?: PackOutDashboardParams
) => {
  const qs = buildPackOutSearchParams(params).toString();
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/pack-outs/dashboard/growers${
      qs ? `?${qs}` : ""
    }`
  );
  return data;
};

export const getPackOutDashboardTrends = async (
  enterpriseId: string,
  params?: PackOutDashboardParams
) => {
  const qs = buildPackOutSearchParams(params).toString();
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/pack-outs/dashboard/trends${
      qs ? `?${qs}` : ""
    }`
  );
  return data;
};

export const getPackOutDashboardDistributions = async (
  enterpriseId: string,
  params?: PackOutDashboardParams
) => {
  const qs = buildPackOutSearchParams(params).toString();
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/pack-outs/dashboard/distributions${
      qs ? `?${qs}` : ""
    }`
  );
  return data;
};

export const getPackOutDashboardBlocks = async (
  enterpriseId: string,
  params?: PackOutDashboardParams
) => {
  const qs = buildPackOutSearchParams(params).toString();
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/pack-outs/dashboard/blocks${
      qs ? `?${qs}` : ""
    }`
  );
  return data;
};

export const getPackOutRuns = async (
  enterpriseId: string,
  params?: PackOutDashboardParams & {
    page?: number;
    limit?: number;
  }
) => {
  const searchParams = buildPackOutSearchParams(params);
  if (params?.page) searchParams.append("page", String(params.page));
  if (params?.limit) searchParams.append("limit", String(params.limit));
  const qs = searchParams.toString();
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/pack-outs${qs ? `?${qs}` : ""}`
  );
  return data;
};

export type PackOutOperationsWindow = "7d" | "30d" | "season" | "all";

export interface PackOutOperationsLine {
  sourceSystem: string;
  lineName: string;
  efficiency: number | null;
  efficiencyMethod: "line_item" | "run_level" | "unavailable";
  packedWeight: number;
  offlineWeight: number;
  runCount: number;
  sparkline: (number | null)[];
  status: "ok" | "warn" | "down";
}

export interface PackOutOperationsAlert {
  id: string;
  severity: "warn" | "down";
  title: string;
  meta: string;
}

export interface PackOutOperationsRun {
  runId: string;
  runDate: string | null;
  growerCode: string | null;
  growerName: string | null;
  variety: string | null;
  lineName: string | null;
  packEfficiency: number | null;
  packedWeight: number;
  approvalStatus: string;
}

export interface PackOutOperationsResponse {
  window: PackOutOperationsWindow;
  windowStart: string | null;
  totalRunCount: number;
  lastRunAt: string | null;
  weightUnitLabel: string;
  lines: PackOutOperationsLine[];
  alerts: PackOutOperationsAlert[];
  recentRuns: PackOutOperationsRun[];
}

export interface PackOutOperationsParams {
  window?: PackOutOperationsWindow;
  startDate?: string;
  endDate?: string;
  crop?: string;
  sourceSystem?: string;
  /** Single grower code; restricts all aggregations on the lens. */
  growerCode?: string;
}

export const getPackOutOperations = async (
  enterpriseId: string,
  params: PackOutOperationsParams = {}
): Promise<PackOutOperationsResponse> => {
  const search = new URLSearchParams();
  if (params.window) search.set("window", params.window);
  if (params.startDate) search.set("startDate", params.startDate);
  if (params.endDate) search.set("endDate", params.endDate);
  if (params.crop) search.set("crop", params.crop);
  if (params.sourceSystem) search.set("sourceSystem", params.sourceSystem);
  if (params.growerCode) search.set("growerCode", params.growerCode);
  const query = search.toString();
  const { data } = await coreAPI.get(
    `/enterprise/${enterpriseId}/pack-outs/operations${
      query ? `?${query}` : ""
    }`
  );
  return data;
};
